(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function idempotencyKey() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    var random = window.crypto && typeof window.crypto.getRandomValues === 'function'
      ? Array.from(window.crypto.getRandomValues(new Uint8Array(16)), function (value) { return value.toString(16).padStart(2, '0'); }).join('')
      : Math.random().toString(36).slice(2) + Date.now().toString(36) + Math.random().toString(36).slice(2);
    return 'tw-' + random.slice(0, 48);
  }

  function operationUrl(form, path) {
    var base = (form.dataset.operationsApiBase || '/apps/trade-waste').replace(/\/$/, '');
    if (!base || (base.charAt(0) !== '/' && !/^https:\/\//i.test(base))) throw new Error('The secure operations service is temporarily unavailable. Please contact Trade Waste.');
    return base + '/' + String(path || '').replace(/^\//, '');
  }

  function operationConnection(form, path) {
    if (form.hasAttribute('data-operations-direct-url')) {
      var base = (form.dataset.operationsDirectUrl || '').replace(/\/$/, '');
      var token = form.dataset.operationsDirectToken || '';
      if (!/^https:\/\//i.test(base) || !token || !/^\/api\/v1\//.test(path)) {
        throw new Error('This form is temporarily unavailable. Please contact Trade Waste.');
      }
      return { url: base + path.replace(/^\/api/, ''), token: token, credentials: 'omit' };
    }
    return { url: operationUrl(form, path), token: form.dataset.operationsClientToken || '', credentials: 'same-origin' };
  }

  function setNestedValue(target, dottedPath, value) {
    var parts = dottedPath.split('.');
    var current = target;
    parts.forEach(function (part, index) {
      var key = /^\d+$/.test(part) ? Number(part) : part;
      if (index === parts.length - 1) {
        current[key] = value;
        return;
      }
      var nextIsArray = /^\d+$/.test(parts[index + 1]);
      if (current[key] === undefined) current[key] = nextIsArray ? [] : {};
      current = current[key];
    });
  }

  function operationPayload(form) {
    var payload = {};
    Array.from(form.elements).forEach(function (field) {
      if (!field.name || field.disabled || field.type === 'submit' || field.type === 'button' || field.type === 'file') return;
      if ((field.type === 'radio') && !field.checked) return;
      var value;
      if (field.type === 'checkbox') value = field.checked;
      else if (field.dataset.boolean !== undefined) value = field.value === 'true';
      else if (field.dataset.number !== undefined) value = Number(field.value);
      else value = String(field.value).trim();
      if (value === '' || (typeof value === 'number' && !Number.isFinite(value))) return;
      setNestedValue(payload, field.name, value);
    });
    return payload;
  }

  async function operationRequest(form, path, payload) {
    var key = form.dataset.idempotencyKey || idempotencyKey();
    form.dataset.idempotencyKey = key;
    // Shopify app proxies can omit non-standard request headers. Send the same
    // key in the signed request body so the operations service can still make
    // every retry idempotent, while retaining the header for direct requests.
    payload = Object.assign({}, payload || {}, { idempotencyKey: key });
    var headers = { 'content-type': 'application/json', accept: 'application/json', 'Idempotency-Key': key };
    var connection = operationConnection(form, path);
    var client = connection.token;
    if (client) headers['x-trade-waste-client'] = client;
    var controller = 'AbortController' in window ? new AbortController() : null;
    var timeout = controller ? window.setTimeout(function () { controller.abort(); }, 30000) : null;
    var response;
    try {
      response = await fetch(connection.url, {
        method: 'POST',
        headers: headers,
        credentials: connection.credentials,
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined
      });
    } catch (error) {
      if (error && error.name === 'AbortError') throw new Error('The request timed out. Please try again using the same details.');
      throw new Error('The secure operations service could not be reached. Please try again.');
    } finally {
      if (timeout) window.clearTimeout(timeout);
    }
    var body;
    try { body = await response.json(); } catch (_error) { body = {}; }
    if (!response.ok) {
      var message = body.error && body.error.message ? body.error.message : 'The request could not be completed.';
      var failure = new Error(message);
      if (body.error && Array.isArray(body.error.issues)) failure.validationIssues = body.error.issues;
      throw failure;
    }
    if (!body || !body.reference) throw new Error('The operations service did not return a reference. Nothing has been confirmed; please try again.');
    return body;
  }

  async function bookingPreparationRequest(form, payload) {
    var api = (form.dataset.deliveryApiUrl || '').replace(/\/$/, '');
    var client = form.dataset.deliveryClientToken || '';
    if (!api || !client) throw new Error('Online booking is temporarily unavailable. Please refresh the page and try again.');
    var key = form.dataset.idempotencyKey || idempotencyKey();
    form.dataset.idempotencyKey = key;
    payload = Object.assign({}, payload || {}, { idempotencyKey: key });
    var controller = 'AbortController' in window ? new AbortController() : null;
    var timeout = controller ? window.setTimeout(function () { controller.abort(); }, 30000) : null;
    var response;
    try {
      response = await fetch(api + '/v1/bookings/prepare', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          accept: 'application/json',
          'Idempotency-Key': key,
          'x-trade-waste-client': client
        },
        credentials: 'omit',
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined
      });
    } catch (error) {
      if (error && error.name === 'AbortError') throw new Error('The request timed out. Please try again using the same details.');
      throw new Error('The secure booking service could not be reached. Please try again.');
    } finally {
      if (timeout) window.clearTimeout(timeout);
    }
    var body;
    try { body = await response.json(); } catch (_error) { body = {}; }
    if (!response.ok) {
      var message = body.error && body.error.message ? body.error.message : 'The booking could not be prepared.';
      throw new Error(message);
    }
    if (!body || !body.reference) throw new Error('The booking service did not return a reference. Nothing has been confirmed; please try again.');
    return body;
  }

  function operationResult(container, response) {
    container.replaceChildren();
    var heading = document.createElement('strong');
    heading.textContent = 'Request received: ' + response.reference;
    container.appendChild(heading);
    var detail = document.createElement('p');
    detail.textContent = response.status === 'ISSUED'
      ? 'Your formal quote has been issued. Keep this reference for WINZ payment.'
      : 'Keep this reference. Trade Waste will use it for all follow up.';
    container.appendChild(detail);
    if (response.expiresAt) {
      var expiry = document.createElement('p');
      expiry.textContent = 'Expiry: ' + new Date(response.expiresAt).toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland' });
      container.appendChild(expiry);
    }
    if (response.totalCents !== undefined && response.currency) {
      var total = document.createElement('p');
      total.textContent = 'Quote total: ' + new Intl.NumberFormat('en-NZ', { style: 'currency', currency: response.currency }).format(response.totalCents / 100);
      container.appendChild(total);
    }
    if (response.pdfUrl) {
      var link = document.createElement('a');
      link.className = 'tw-btn tw-btn--navy';
      link.href = response.pdfUrl;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = 'Download formal quote PDF';
      container.appendChild(link);
    }
    container.hidden = false;
    container.focus();
  }

  function drawer() {
    var drawerElement = document.querySelector('[data-drawer]');
    var scrim = document.querySelector('[data-scrim]');
    var openButton = document.querySelector('[data-drawer-open]');
    if (!drawerElement || !scrim || !openButton) return;

    function setDrawer(open) {
      var value = open ? 'true' : 'false';
      drawerElement.dataset.open = value;
      drawerElement.setAttribute('aria-hidden', String(!open));
      scrim.dataset.open = value;
      openButton.setAttribute('aria-expanded', value);
      if (open) {
        var closeButton = drawerElement.querySelector('[data-drawer-close]');
        if (closeButton) closeButton.focus();
      } else {
        openButton.focus();
      }
    }

    openButton.addEventListener('click', function () { setDrawer(true); });
    drawerElement.querySelectorAll('[data-drawer-close]').forEach(function (button) {
      button.addEventListener('click', function () { setDrawer(false); });
    });
    drawerElement.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () { setDrawer(false); });
    });
    scrim.addEventListener('click', function () { setDrawer(false); });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawerElement.dataset.open === 'true') setDrawer(false);
    });
  }

  function navigationDropdowns() {
    var hoverNavigation = window.matchMedia('(hover: hover) and (pointer: fine)');
    var dropdowns = Array.from(document.querySelectorAll('.tw-nav [data-nav-dropdown]'));

    dropdowns.forEach(function (dropdown) {
      if (dropdown.dataset.hoverReady === 'true') return;
      dropdown.dataset.hoverReady = 'true';

      dropdown.addEventListener('mouseenter', function () {
        if (!hoverNavigation.matches) return;
        dropdowns.forEach(function (other) {
          if (other !== dropdown) other.open = false;
        });
        dropdown.open = true;
      });
      dropdown.addEventListener('mouseleave', function () {
        if (hoverNavigation.matches) dropdown.open = false;
      });
      dropdown.addEventListener('focusout', function (event) {
        if (!dropdown.contains(event.relatedTarget)) dropdown.open = false;
      });
      dropdown.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          dropdown.open = false;
          var summary = dropdown.querySelector('summary');
          if (summary) summary.focus();
        }
      });
    });
  }

  function sliders() {
    document.querySelectorAll('[data-slider]').forEach(function (root) {
      if (root.dataset.sliderReady) return;
      root.dataset.sliderReady = 'true';
      var slides = Array.from(root.querySelectorAll('[data-slide]'));
      var dots = Array.from(root.querySelectorAll('[data-dot]'));
      if (slides.length < 2) return;
      var current = 0;
      var timer;
      var delay = parseInt(root.dataset.interval, 10) || 6000;

      function show(index) {
        current = (index + slides.length) % slides.length;
        slides.forEach(function (slide, position) { slide.classList.toggle('is-active', position === current); });
        dots.forEach(function (dot, position) { dot.classList.toggle('is-active', position === current); });
      }
      function start() {
        if (reduceMotion) return;
        window.clearInterval(timer);
        timer = window.setInterval(function () { show(current + 1); }, delay);
      }
      dots.forEach(function (dot, index) {
        dot.addEventListener('click', function () { show(index); start(); });
      });
      root.addEventListener('mouseenter', function () { window.clearInterval(timer); });
      root.addEventListener('mouseleave', start);
      start();
    });
  }

  function reveal() {
    var elements = document.querySelectorAll('.tw-reveal:not(.is-seen)');
    if (!elements.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      elements.forEach(function (element) { element.classList.add('is-seen'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-seen');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    elements.forEach(function (element) { observer.observe(element); });
  }

  function productGalleries() {
    document.querySelectorAll('[data-product-gallery]').forEach(function (gallery) {
      if (gallery.dataset.galleryReady) return;
      gallery.dataset.galleryReady = 'true';
      var images = Array.from(gallery.querySelectorAll('[data-gallery-image]'));
      var thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var index = parseInt(thumb.dataset.index, 10) || 0;
          images.forEach(function (image, position) { image.classList.toggle('is-active', position === index); });
          thumbs.forEach(function (item, position) { item.classList.toggle('is-active', position === index); });
        });
      });
    });
  }

  function variantPrices() {
    document.querySelectorAll('[data-booking-form]').forEach(function (form) {
      var select = form.querySelector('[data-variant-select]');
      var product = form.closest('.tw-product');
      var price = product && product.querySelector('[data-price]');
      if (!select || !price) return;
      select.addEventListener('change', function () {
        var option = select.options[select.selectedIndex];
        if (option && option.dataset.price) price.textContent = option.dataset.price;
      });
    });
  }

  var validationId = 0;

  function notifyFormState(form, type) {
    if (!form || typeof form.dispatchEvent !== 'function' || typeof CustomEvent !== 'function') return;
    form.dispatchEvent(new CustomEvent(type));
  }

  function validatableControls(form) {
    return Array.from(form.elements || []).filter(function (control) {
      if (!control || !control.tagName) return false;
      var tag = control.tagName.toLowerCase();
      if (tag !== 'input' && tag !== 'select' && tag !== 'textarea') return false;
      var type = String(control.type || '').toLowerCase();
      return type !== 'hidden' && type !== 'submit' && type !== 'button' && type !== 'reset' && type !== 'image';
    });
  }

  function unavailableControl(control) {
    if (!control || control.disabled || control.hidden) return true;
    if (typeof control.matches === 'function' && control.matches(':disabled')) return true;
    return Boolean(typeof control.closest === 'function' && control.closest('[hidden], [aria-hidden="true"]'));
  }

  function controlGroup(control, form) {
    if (String(control.type || '').toLowerCase() !== 'radio' || !control.name) return [control];
    return validatableControls(form).filter(function (candidate) {
      return String(candidate.type || '').toLowerCase() === 'radio' && candidate.name === control.name;
    });
  }

  function controlLabel(control) {
    var explicitLabel = control.getAttribute && String(control.getAttribute('data-validation-label') || '').replace(/\s+/g, ' ').trim();
    if (explicitLabel) return explicitLabel;
    if (String(control.type || '').toLowerCase() === 'checkbox') return 'this required statement';
    if (String(control.type || '').toLowerCase() === 'radio') return 'an option';
    var label = control.labels && control.labels.length ? control.labels[0] : null;
    var text = label ? String(label.textContent || '') : '';
    text = text.replace(/\bRequired\b/g, '').replace(/\s+/g, ' ').trim();
    if (text) return text;
    return String(control.name || 'this field')
      .replace(/^properties\[|\]$/g, '')
      .replace(/[._-]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .trim()
      .toLowerCase() || 'this field';
  }

  function sentenceLabel(control) {
    var label = controlLabel(control);
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  function messageLabel(control) {
    var label = controlLabel(control);
    if (/^(WINZ|NZBN|GST)\b/.test(label)) return label;
    return label.charAt(0).toLowerCase() + label.slice(1);
  }

  function validationMessage(control) {
    var validity = control.validity || {};
    var type = String(control.type || '').toLowerCase();
    var label = messageLabel(control);
    if (validity.customError) return control.validationMessage || 'Review ' + label + ' and try again.';
    if (validity.valueMissing) {
      if (type === 'checkbox') return 'Confirm this required statement.';
      if (type === 'radio') return 'Choose an option to continue.';
      if (control.tagName && control.tagName.toLowerCase() === 'select') return 'Choose ' + label + '.';
      return 'Enter ' + label + '.';
    }
    if (validity.typeMismatch) {
      if (type === 'email') return 'Enter a valid email address.';
      if (type === 'url') return 'Enter a complete website address, including https://.';
      return 'Enter a valid ' + label + '.';
    }
    if (validity.patternMismatch) {
      if (control.hasAttribute && (control.hasAttribute('data-delivery-postcode') || control.getAttribute('autocomplete') === 'postal-code')) return 'Enter a four-digit postcode.';
      return sentenceLabel(control) + ' is not in the expected format.';
    }
    if (validity.tooShort) return sentenceLabel(control) + ' must be at least ' + control.minLength + ' characters.';
    if (validity.tooLong) return sentenceLabel(control) + ' must be no more than ' + control.maxLength + ' characters.';
    if (validity.rangeUnderflow) {
      if (control.hasAttribute && control.hasAttribute('data-delivery-date') && /^\d{4}-\d{2}-\d{2}$/.test(control.min || '')) {
        var minimumParts = control.min.split('-').map(Number);
        var minimumDate = new Date(Date.UTC(minimumParts[0], minimumParts[1] - 1, minimumParts[2]));
        var minimumLabel = new Intl.DateTimeFormat('en-NZ', { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(minimumDate);
        return 'Booking must be from ' + minimumLabel + ' onwards.';
      }
      return sentenceLabel(control) + ' must be ' + control.min + ' or more.';
    }
    if (validity.rangeOverflow) return sentenceLabel(control) + ' must be ' + control.max + ' or less.';
    if (validity.stepMismatch) return 'Enter a permitted value for ' + label + '.';
    if (validity.badInput || type === 'date') return 'Enter a valid ' + label + '.';
    return control.validationMessage || 'Review ' + label + ' and try again.';
  }

  function ensureControlId(control) {
    if (control.id) return control.id;
    validationId += 1;
    control.id = 'tw-required-field-' + validationId;
    return control.id;
  }

  function choiceContainer(control) {
    if (typeof control.closest !== 'function') return null;
    var type = String(control.type || '').toLowerCase();
    if (type === 'checkbox') return control.closest('.tw-checkbox');
    if (type === 'radio') return control.closest('.tw-fieldset');
    return null;
  }

  function errorPlacement(control) {
    if (typeof control.closest !== 'function') return control.parentElement;
    var row = control.closest('.tw-form-row');
    if (row) return row;
    var type = String(control.type || '').toLowerCase();
    if (type === 'checkbox') return control.closest('.tw-checkbox') || control;
    if (type === 'radio') return control.closest('.tw-fieldset') || control;
    var addon = control.closest('.tw-addon');
    if (addon) return addon;
    return control;
  }

  function ensureInlineError(control) {
    var id = ensureControlId(control) + '-validation-error';
    var form = control.form || (typeof control.closest === 'function' ? control.closest('form') : null);
    var existing = form && typeof form.querySelector === 'function' ? form.querySelector('#' + id) : null;
    if (existing) return existing;
    var error = document.createElement('p');
    error.id = id;
    error.className = 'tw-field-error';
    error.setAttribute('aria-live', 'polite');
    error.hidden = true;
    var placement = errorPlacement(control);
    if (placement && placement.classList && placement.classList.contains('tw-checkbox')) error.classList.add('tw-field-error--choice');
    if (placement && placement.classList && (placement.classList.contains('tw-form-row') || placement.classList.contains('tw-addon'))) placement.appendChild(error);
    else if (placement && typeof placement.insertAdjacentElement === 'function') placement.insertAdjacentElement('afterend', error);
    else if (placement && placement.parentElement) placement.parentElement.appendChild(error);
    return error;
  }

  function describedBy(control) {
    return String(control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
  }

  function setErrorDescription(control, errorId) {
    var ids = describedBy(control);
    if (ids.indexOf(errorId) === -1) ids.push(errorId);
    control.setAttribute('aria-describedby', ids.join(' '));
  }

  function removeErrorDescription(control, errorId) {
    var ids = describedBy(control).filter(function (id) { return id !== errorId; });
    if (ids.length) control.setAttribute('aria-describedby', ids.join(' '));
    else control.removeAttribute('aria-describedby');
  }

  function showControlError(control, form) {
    var group = controlGroup(control, form);
    var representative = group[0] || control;
    var error = ensureInlineError(representative);
    error.textContent = validationMessage(representative);
    error.hidden = false;
    group.forEach(function (member) {
      member.dataset.touched = 'true';
      member.setAttribute('aria-invalid', 'true');
      setErrorDescription(member, error.id);
      var container = choiceContainer(member);
      if (container) container.dataset.validationInvalid = 'true';
    });
  }

  function clearControlError(control, form) {
    var group = controlGroup(control, form);
    var representative = group[0] || control;
    var errorId = ensureControlId(representative) + '-validation-error';
    var error = form && typeof form.querySelector === 'function' ? form.querySelector('#' + errorId) : null;
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
    group.forEach(function (member) {
      member.removeAttribute('aria-invalid');
      removeErrorDescription(member, errorId);
      var container = choiceContainer(member);
      if (container) delete container.dataset.validationInvalid;
    });
  }

  function markControlTouched(control, form) {
    controlGroup(control, form).forEach(function (member) { member.dataset.touched = 'true'; });
  }

  function controlTouched(control, form) {
    return controlGroup(control, form).some(function (member) { return member.dataset.touched === 'true'; });
  }

  function validateTouchedControl(control, forceTouched) {
    var form = control.form || (typeof control.closest === 'function' ? control.closest('form') : null);
    if (!form) return true;
    var group = controlGroup(control, form);
    var representative = group[0] || control;
    if (forceTouched) markControlTouched(representative, form);
    if (group.every(unavailableControl)) {
      clearControlError(representative, form);
      return true;
    }
    var valid = group.some(function (member) { return !unavailableControl(member) && member.checkValidity(); });
    if (!valid && controlTouched(representative, form)) showControlError(representative, form);
    else clearControlError(representative, form);
    return valid;
  }

  function uniqueValidationControls(form) {
    var radioNames = new Set();
    return validatableControls(form).filter(function (control) {
      if (unavailableControl(control)) return false;
      if (String(control.type || '').toLowerCase() !== 'radio' || !control.name) return true;
      if (radioNames.has(control.name)) return false;
      radioNames.add(control.name);
      return true;
    });
  }

  function invalidControls(form) {
    return uniqueValidationControls(form).filter(function (control) { return !validateTouchedControl(control, false); });
  }

  function ensureValidationSummary(form) {
    var summary = form.querySelector('[data-validation-summary]');
    if (summary) return summary;
    summary = document.createElement('div');
    summary.className = 'tw-validation-summary';
    summary.dataset.validationSummary = '';
    summary.setAttribute('role', 'alert');
    summary.setAttribute('aria-live', 'assertive');
    summary.setAttribute('tabindex', '-1');
    summary.hidden = true;
    if (typeof form.prepend === 'function') form.prepend(summary);
    else form.insertBefore(summary, form.firstChild);
    return summary;
  }

  function renderValidationSummary(form, invalid, focusSummary) {
    var summary = ensureValidationSummary(form);
    summary.replaceChildren();
    if (!invalid.length) {
      summary.hidden = true;
      return;
    }
    var heading = document.createElement('h2');
    heading.className = 'tw-validation-summary__title';
    heading.textContent = 'Please check the highlighted fields';
    var intro = document.createElement('p');
    intro.textContent = 'Correct the following before continuing:';
    var list = document.createElement('ul');
    invalid.forEach(function (control) {
      var item = document.createElement('li');
      var link = document.createElement('a');
      link.href = '#' + ensureControlId(control);
      link.textContent = validationMessage(control);
      link.addEventListener('click', function (event) {
        event.preventDefault();
        control.focus();
      });
      item.appendChild(link);
      list.appendChild(item);
    });
    summary.append(heading, intro, list);
    summary.hidden = false;
    if (focusSummary) {
      summary.focus();
      summary.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    }
  }

  function refreshSubmittedSummary(form) {
    if (form.dataset.validationSubmitted !== 'true') return;
    renderValidationSummary(form, invalidControls(form), false);
  }

  function validateTouchedControls(form, controls, focusFirst) {
    var invalid = [];
    controls.filter(Boolean).forEach(function (control) {
      markControlTouched(control, form);
      if (!validateTouchedControl(control, true)) invalid.push(control);
    });
    if (invalid.length && focusFirst) invalid[0].focus();
    return invalid.length === 0;
  }

  function validateTouchedForm(form, focusSummary) {
    form.dataset.validationSubmitted = 'true';
    var invalid = uniqueValidationControls(form).filter(function (control) {
      var valid = validateTouchedControl(control, false);
      if (!valid) {
        markControlTouched(control, form);
        validateTouchedControl(control, true);
      }
      return !valid;
    });
    renderValidationSummary(form, invalid, Boolean(focusSummary));
    return invalid.length === 0;
  }

  function clearTouchedValidation(form) {
    validatableControls(form).forEach(function (control) {
      clearOperationServerValidity(control);
      controlGroup(control, form).forEach(function (member) { delete member.dataset.touched; });
      clearControlError(control, form);
    });
    delete form.dataset.validationSubmitted;
    renderValidationSummary(form, [], false);
  }

  function clearOperationServerValidity(control) {
    if (control.dataset.operationServerValidity !== 'true') return;
    control.setCustomValidity('');
    delete control.dataset.operationServerValidity;
  }

  function showOperationServerValidation(form, error) {
    if (!error || !Array.isArray(error.validationIssues)) return false;
    var matched = 0;
    error.validationIssues.forEach(function (issue) {
      var control = form.elements.namedItem(issue.field);
      if (control && control.type === 'hidden' && typeof control.closest === 'function') {
        var addressRoot = control.closest('[data-address-lookup-root]');
        control = addressRoot && addressRoot.querySelector('[data-address-lookup-input]');
      }
      if (!control || typeof control.setCustomValidity !== 'function') return;
      var detail = issue.message || 'Review this value.';
      if (issue.code === 'too_small' && Number.isInteger(issue.minimum)) {
        detail = 'must be at least ' + issue.minimum + ' characters.';
      } else if (issue.code === 'too_big' && Number.isInteger(issue.maximum)) {
        detail = 'must be no more than ' + issue.maximum + ' characters.';
      }
      control.setCustomValidity(sentenceLabel(control) + (detail.startsWith('must ') ? ' ' : ': ') + detail);
      control.dataset.operationServerValidity = 'true';
      matched += 1;
    });
    if (!matched) return false;
    validateTouchedForm(form, true);
    return true;
  }

  function ensureRequiredMarker(control) {
    if (!control.required || !control.labels || !control.labels.length) return;
    var label = control.labels[0];
    if (label.querySelector && label.querySelector('.tw-required-marker')) return;
    var marker = document.createElement('span');
    marker.className = 'tw-required-marker';
    marker.setAttribute('aria-hidden', 'true');
    marker.textContent = 'Required';
    if (label.classList && label.classList.contains('tw-checkbox')) {
      var text = label.querySelector('span');
      if (text && typeof text.prepend === 'function') text.prepend(marker);
      else label.appendChild(marker);
    } else if (typeof label.contains === 'function' && label.contains(control) && typeof label.insertBefore === 'function') {
      label.insertBefore(marker, control);
    } else {
      label.appendChild(marker);
    }
  }

  function termsScrollGates() {
    document.querySelectorAll('[data-terms-scroll-gate]').forEach(function (gate) {
      if (gate.dataset.termsScrollReady === 'true') return;
      var panel = gate.querySelector('[data-terms-scroll-content]');
      var checkbox = gate.querySelector('[data-terms-gated]');
      var status = gate.querySelector('[data-terms-scroll-status]');
      var lockMessage = gate.querySelector('[data-terms-lock-message]');
      var form = gate.closest('form');
      if (!panel || !checkbox || !status || !form) return;
      gate.dataset.termsScrollReady = 'true';
      var sourceAvailable = gate.dataset.termsSourceAvailable === 'true';
      var unlocked = false;

      function lock() {
        unlocked = false;
        checkbox.checked = false;
        checkbox.setAttribute('aria-disabled', 'true');
        checkbox.dataset.termsLocked = 'true';
        checkbox.setCustomValidity(sourceAvailable
          ? 'Scroll through the service terms before accepting them.'
          : 'The service terms are unavailable. Contact Trade Waste before continuing.');
        gate.classList.add('is-locked');
        status.textContent = sourceAvailable
          ? 'Scroll through the terms and tick the box to accept.'
          : 'The service terms are unavailable. Contact Trade Waste before continuing.';
        if (lockMessage) lockMessage.textContent = status.textContent;
        notifyFormState(form, 'trade-waste:terms-state');
      }

      function unlock() {
        if (!sourceAvailable || unlocked) return;
        unlocked = true;
        delete checkbox.dataset.termsLocked;
        checkbox.removeAttribute('aria-disabled');
        checkbox.setCustomValidity('');
        gate.classList.remove('is-locked');
        status.textContent = 'Terms reviewed. You can now accept them.';
        if (lockMessage) lockMessage.textContent = status.textContent;
        validateTouchedControl(checkbox, false);
        refreshSubmittedSummary(form);
        notifyFormState(form, 'trade-waste:terms-state');
      }

      function atEnd() {
        return panel.scrollHeight <= panel.clientHeight + 2
          || panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 4;
      }

      function evaluate() {
        if (atEnd()) unlock();
      }

      function preventLockedAcceptance(event) {
        if (unlocked) return;
        event.preventDefault();
        checkbox.checked = false;
        markControlTouched(checkbox, form);
        validateTouchedControl(checkbox, true);
        refreshSubmittedSummary(form);
      }

      panel.addEventListener('scroll', evaluate);
      checkbox.addEventListener('click', preventLockedAcceptance);
      checkbox.addEventListener('keydown', function (event) {
        if (event.key === ' ' || event.key === 'Enter') preventLockedAcceptance(event);
      });
      checkbox.addEventListener('change', function (event) {
        if (!unlocked && checkbox.checked) preventLockedAcceptance(event);
      });
      form.addEventListener('reset', function () {
        window.setTimeout(lock, 0);
      });
      lock();
      (window.requestAnimationFrame || function (callback) { window.setTimeout(callback, 0); })(evaluate);
    });
  }

  window.TradeWasteTermsGate = Object.freeze({ initialise: termsScrollGates });

  function touchedValidation() {
    document.querySelectorAll('form').forEach(function (form) {
      if (form.dataset.touchedValidationReady === 'true') return;
      var controls = validatableControls(form);
      if (!controls.length) return;
      form.dataset.touchedValidationReady = 'true';
      form.noValidate = true;
      ensureValidationSummary(form);
      controls.forEach(function (control) {
        controlGroup(control, form).forEach(function (member) {
          delete member.dataset.touched;
        });
        clearControlError(control, form);
        ensureRequiredMarker(control);
        control.addEventListener('blur', function () {
          markControlTouched(control, form);
          validateTouchedControl(control, true);
          refreshSubmittedSummary(form);
        });
        control.addEventListener('input', function () {
          clearOperationServerValidity(control);
          if (!controlTouched(control, form)) return;
          validateTouchedControl(control, false);
          refreshSubmittedSummary(form);
        });
        control.addEventListener('change', function () {
          clearOperationServerValidity(control);
          if (!controlTouched(control, form)) return;
          validateTouchedControl(control, false);
          refreshSubmittedSummary(form);
        });
      });
      form.addEventListener('submit', function (event) {
        if (form.matches && form.matches('[data-cart-form]') && event.submitter && event.submitter.name !== 'checkout') return;
        if (validateTouchedForm(form, true)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, true);
      form.addEventListener('reset', function () {
        window.setTimeout(function () { clearTouchedValidation(form); }, 0);
      });
    });
  }

  window.TradeWasteFormValidation = Object.freeze({
    clear: clearTouchedValidation,
    initialise: touchedValidation,
    validateControl: validateTouchedControl,
    validateControls: validateTouchedControls,
    validateForm: validateTouchedForm,
    showServerIssues: showOperationServerValidation
  });

  function utcDate(year, month, day) { return new Date(Date.UTC(year, month - 1, day)); }
  function dateKey(date) {
    return date.getUTCFullYear() + '-' + String(date.getUTCMonth() + 1).padStart(2, '0') + '-' + String(date.getUTCDate()).padStart(2, '0');
  }
  function addDays(date, days) { var next = new Date(date.getTime()); next.setUTCDate(next.getUTCDate() + days); return next; }
  function nthWeekday(year, month, weekday, nth) {
    var date = utcDate(year, month, 1);
    var offset = (weekday - date.getUTCDay() + 7) % 7;
    return addDays(date, offset + ((nth - 1) * 7));
  }
  function easterSunday(year) {
    var a = year % 19;
    var b = Math.floor(year / 100);
    var c = year % 100;
    var d = Math.floor(b / 4);
    var e = b % 4;
    var f = Math.floor((b + 8) / 25);
    var g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4);
    var k = c % 4;
    var l = (32 + 2 * e + 2 * i - h - k) % 7;
    var m = Math.floor((a + 11 * h + 22 * l) / 451);
    var month = Math.floor((h + l - 7 * m + 114) / 31);
    var day = ((h + l - 7 * m + 114) % 31) + 1;
    return utcDate(year, month, day);
  }
  function addMondayisedSingle(set, date) {
    set.add(dateKey(date));
    if (date.getUTCDay() === 6) set.add(dateKey(addDays(date, 2)));
    if (date.getUTCDay() === 0) set.add(dateKey(addDays(date, 1)));
  }
  function addMondayisedPair(set, first) {
    var second = addDays(first, 1);
    set.add(dateKey(first));
    set.add(dateKey(second));
    if (first.getUTCDay() === 5) {
      set.add(dateKey(addDays(second, 2)));
    } else if (first.getUTCDay() === 6) {
      set.add(dateKey(addDays(first, 2)));
      set.add(dateKey(addDays(second, 2)));
    } else if (first.getUTCDay() === 0) {
      set.add(dateKey(addDays(first, 2)));
      set.add(dateKey(second));
    }
  }
  function publicHolidays(year) {
    var dates = new Set();
    addMondayisedPair(dates, utcDate(year, 1, 1));
    addMondayisedSingle(dates, utcDate(year, 2, 6));
    var easter = easterSunday(year);
    dates.add(dateKey(addDays(easter, -2)));
    dates.add(dateKey(addDays(easter, 1)));
    addMondayisedSingle(dates, utcDate(year, 4, 25));
    dates.add(dateKey(nthWeekday(year, 6, 1, 1)));
    var matariki = {
      2022: '2022-06-24', 2023: '2023-07-14', 2024: '2024-06-28', 2025: '2025-06-20',
      2026: '2026-07-10', 2027: '2027-06-25', 2028: '2028-07-14', 2029: '2029-07-06',
      2030: '2030-06-21', 2031: '2031-07-11', 2032: '2032-07-02', 2033: '2033-06-24',
      2034: '2034-07-07', 2035: '2035-06-29', 2036: '2036-07-18', 2037: '2037-07-10',
      2038: '2038-06-25', 2039: '2039-07-15', 2040: '2040-07-06', 2041: '2041-07-19',
      2042: '2042-07-11', 2043: '2043-07-03', 2044: '2044-06-24', 2045: '2045-07-07',
      2046: '2046-06-29', 2047: '2047-07-19', 2048: '2048-07-03', 2049: '2049-06-25',
      2050: '2050-07-15', 2051: '2051-06-30', 2052: '2052-06-21'
    };
    if (matariki[year]) dates.add(matariki[year]);
    dates.add(dateKey(nthWeekday(year, 10, 1, 4)));
    var firstTuesday = nthWeekday(year, 11, 2, 1);
    dates.add(dateKey(addDays(firstTuesday, 10)));
    addMondayisedPair(dates, utcDate(year, 12, 25));
    return dates;
  }
  function businessDateRules(input) {
    var custom = (input.dataset.blackoutDates || '').split(/[\s,]+/).filter(Boolean);
    var customSet = new Set(custom);
    function blocked(date) {
      var weekday = date.getUTCDay();
      return weekday === 0 || weekday === 6 || publicHolidays(date.getUTCFullYear()).has(dateKey(date)) || customSet.has(dateKey(date));
    }
    return { blocked: blocked };
  }
  function nzNowParts() {
    var parts = new Intl.DateTimeFormat('en-NZ', {
      timeZone: 'Pacific/Auckland', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23'
    }).formatToParts(new Date()).reduce(function (output, part) { output[part.type] = part.value; return output; }, {});
    return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day), hour: Number(parts.hour) };
  }
  function earliestDeliveryDate(cutoffHour, input) {
    var now = nzNowParts();
    var date = utcDate(now.year, now.month, now.day);
    var requiredBusinessDays = now.hour >= cutoffHour ? 2 : 1;
    var rules = businessDateRules(input);
    while (requiredBusinessDays > 0) {
      date = addDays(date, 1);
      if (!rules.blocked(date)) requiredBusinessDays -= 1;
    }
    return date;
  }

  var availabilityRequests = new Map();

  function operationsBaseForInput(input) {
    var root = input.closest('[data-operations-api-base]');
    var base = root && String(root.dataset.operationsApiBase || '').trim().replace(/\/$/, '');
    if (!base || (base.charAt(0) !== '/' && !/^https:\/\//i.test(base))) return '';
    return base;
  }

  function availabilityRequest(base) {
    if (!base) return Promise.resolve(new Map());
    if (availabilityRequests.has(base)) return availabilityRequests.get(base);
    var request = fetch(base + '/api/v1/availability?days=90', {
      method: 'GET', headers: { accept: 'application/json' }, credentials: 'same-origin'
    }).then(function (response) {
      if (!response.ok) throw new Error('Availability could not be loaded.');
      return response.json();
    }).then(function (body) {
      var days = Array.isArray(body && body.days) ? body.days : [];
      var result = new Map();
      days.forEach(function (day) {
        if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(String(day.date || ''))) return;
        if (!['AVAILABLE', 'LIMITED', 'SOLD_OUT', 'CLOSED'].includes(day.delivery)) return;
        if (!['AVAILABLE', 'LIMITED', 'SOLD_OUT', 'CLOSED'].includes(day.pickup)) return;
        result.set(day.date, {
          delivery: day.delivery,
          pickup: day.pickup,
          reason: typeof day.reason === 'string' ? day.reason.slice(0, 160) : ''
        });
      });
      return result;
    }).catch(function () {
      // Static public-holiday rules and authoritative server-side capacity
      // checks remain in force when the advisory calendar cannot be loaded.
      return new Map();
    });
    availabilityRequests.set(base, request);
    return request;
  }

  function availabilityChannels(input) {
    if (input.hasAttribute('data-delivery-date')) return ['delivery'];
    var form = input.closest('[data-service-request-form]');
    var path = String(form && form.dataset.operationPath || '');
    return path.indexOf('changeover') >= 0 ? ['pickup', 'delivery'] : ['pickup'];
  }

  function availabilityStatus(input) {
    var existing = input.parentElement && input.parentElement.querySelector('[data-date-availability-status]');
    if (existing) return existing;
    var status = document.createElement('p');
    status.className = 'tw-form-help tw-date-availability';
    status.dataset.dateAvailabilityStatus = '';
    status.setAttribute('aria-live', 'polite');
    if (input.parentElement) input.parentElement.appendChild(status);
    return status;
  }

  function remoteDateMessage(day, channels) {
    if (!day) return '';
    var values = channels.map(function (channel) { return day[channel]; });
    if (values.includes('CLOSED')) return day.reason || 'This date is unavailable. Choose another date.';
    if (values.includes('SOLD_OUT')) return 'This date is sold out. Choose another date.';
    return '';
  }

  function updateAvailabilityStatus(status, day, channels, blocked) {
    if (!status) return;
    if (blocked || !day) {
      status.textContent = '';
      return;
    }
    var values = channels.map(function (channel) { return day[channel]; });
    var service = channels.length > 1 ? 'pickup and delivery' : (channels[0] === 'pickup' ? 'pickup' : 'delivery');
    status.textContent = values.includes('LIMITED')
      ? 'Limited availability for ' + service + '.'
      : 'Available for ' + service + '.';
  }

  function dateInputs() {
    document.querySelectorAll('[data-delivery-date]').forEach(function (input) {
      var form = input.closest('[data-booking-form]');
      var cutoff = Number(form && form.dataset.cutoffHour) || 17;
      var availability = new Map();
      var channels = availabilityChannels(input);
      var status = availabilityStatus(input);
      function refreshDeliveryMinimum() {
        var minimum = dateKey(earliestDeliveryDate(cutoff, input));
        if (input.min !== minimum) input.min = minimum;
        return minimum;
      }
      refreshDeliveryMinimum();
      function updateDeliveryDateValidity() {
        refreshDeliveryMinimum();
        input.setCustomValidity('');
        if (input.value) {
          var selected = new Date(input.value + 'T00:00:00Z');
          var rules = businessDateRules(input);
          var staticallyBlocked = rules.blocked(selected);
          var day = availability.get(input.value);
          input.setCustomValidity(staticallyBlocked
            ? 'Choose an available weekday. Weekends and public holidays are unavailable.'
            : remoteDateMessage(day, channels));
          updateAvailabilityStatus(status, day, channels, staticallyBlocked || !input.checkValidity());
        } else {
          status.textContent = '';
        }
        validateTouchedControl(input, false);
        notifyFormState(form, 'trade-waste:availability-state');
      }
      input.addEventListener('input', updateDeliveryDateValidity);
      input.addEventListener('change', updateDeliveryDateValidity);
      if (form) form.addEventListener('submit', updateDeliveryDateValidity, true);
      window.addEventListener('focus', updateDeliveryDateValidity);
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) updateDeliveryDateValidity();
      });
      window.setInterval(refreshDeliveryMinimum, 60000);
      availabilityRequest(operationsBaseForInput(input)).then(function (days) {
        availability = days;
        updateDeliveryDateValidity();
      });
    });
    document.querySelectorAll('[data-request-date]').forEach(function (input) {
      var now = nzNowParts();
      var minimum = Number(input.dataset.minimumNoticeDays) || 1;
      var candidate = utcDate(now.year, now.month, now.day);
      var rules = businessDateRules(input);
      var availability = new Map();
      var channels = availabilityChannels(input);
      var status = availabilityStatus(input);
      while (minimum > 0) {
        candidate = addDays(candidate, 1);
        if (!rules.blocked(candidate)) minimum -= 1;
      }
      input.min = dateKey(candidate);
      function updateRequestDateValidity() {
        input.setCustomValidity('');
        if (input.value) {
          var selected = new Date(input.value + 'T00:00:00Z');
          var staticallyBlocked = rules.blocked(selected);
          var day = availability.get(input.value);
          input.setCustomValidity(staticallyBlocked
            ? 'Choose an available weekday. Weekends and public holidays are unavailable.'
            : remoteDateMessage(day, channels));
          updateAvailabilityStatus(status, day, channels, staticallyBlocked || !input.checkValidity());
        } else {
          status.textContent = '';
        }
        validateTouchedControl(input, false);
      }
      input.addEventListener('input', updateRequestDateValidity);
      input.addEventListener('change', updateRequestDateValidity);
      availabilityRequest(operationsBaseForInput(input)).then(function (days) {
        availability = days;
        updateRequestDateValidity();
      });
    });
  }

  function addressLookups() {
    document.querySelectorAll('[data-address-lookup-root]').forEach(function (root) {
      if (root.dataset.addressLookupReady === 'true') return;
      var form = root.closest('form');
      var input = root.querySelector('[data-address-lookup-input]');
      var suggestions = root.querySelector('[data-address-lookup-suggestions]');
      var panel = root.querySelector('[data-address-lookup-panel]');
      var status = root.querySelector('[data-address-lookup-status]');
      var full = root.querySelector('[data-address-lookup-full]');
      var address1 = root.querySelector('[data-address-lookup-address1]');
      var suburb = root.querySelector('[data-address-lookup-suburb]');
      var city = root.querySelector('[data-address-lookup-city]');
      var postcode = root.querySelector('[data-address-lookup-postcode]');
      var placeId = root.querySelector('[data-address-lookup-place-id]');
      var sessionField = root.querySelector('[data-address-lookup-session-token]');
      var api = String(root.dataset.addressApiUrl || '').replace(/\/$/, '');
      var client = root.dataset.addressClientToken || '';
      var timer = null;
      var controller = null;
      var values = [];
      var active = -1;
      var sessionToken = '';
      var selected = false;
      if (!form || !input || !suggestions || !panel || !status || !address1 || !suburb || !city || !postcode) return;
      root.dataset.addressLookupReady = 'true';

      function setStatus(text, error) {
        status.textContent = text || '';
        status.classList.toggle('tw-address-autocomplete__status--error', Boolean(error));
      }
      function clearOutput() {
        [full, address1, suburb, city, postcode, placeId].forEach(function (field) {
          if (field) field.value = '';
        });
      }
      function closeSuggestions() {
        values = [];
        active = -1;
        suggestions.replaceChildren();
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      }
      function cancel(resetSession) {
        if (timer) window.clearTimeout(timer);
        timer = null;
        if (controller) controller.abort();
        controller = null;
        input.setAttribute('aria-busy', 'false');
        if (resetSession) {
          sessionToken = '';
          if (sessionField) sessionField.value = '';
        }
      }
      function ensureSession() {
        if (!sessionToken) {
          sessionToken = idempotencyKey().replace(/[^A-Za-z0-9_-]/g, '').slice(0, 36);
          if (sessionField) sessionField.value = sessionToken;
        }
        return sessionToken;
      }
      function setActive(index) {
        var options = Array.from(suggestions.querySelectorAll('[role="option"]'));
        if (!options.length) return;
        active = (index + options.length) % options.length;
        options.forEach(function (option, position) {
          option.setAttribute('aria-selected', String(position === active));
        });
        input.setAttribute('aria-activedescendant', options[active].id);
        if (typeof options[active].scrollIntoView === 'function') options[active].scrollIntoView({ block: 'nearest' });
      }
      function canonicalAddress(body) {
        var structured = body && body.structuredAddress;
        if (!structured || typeof structured.address1 !== 'string' || structured.address1.trim().length < 4
          || typeof structured.city !== 'string' || !structured.city.trim()
          || typeof structured.postcode !== 'string' || !/^\d{4}$/.test(structured.postcode.trim())
          || typeof body.validatedAddress !== 'string' || !body.validatedAddress.trim()) return null;
        return {
          full: body.validatedAddress.trim(),
          address1: structured.address1.trim(),
          suburb: typeof structured.suburb === 'string' ? structured.suburb.trim() : '',
          city: structured.city.trim(),
          postcode: structured.postcode.trim(),
          placeId: typeof body.placeId === 'string' ? body.placeId.trim() : ''
        };
      }
      async function confirmSuggestion(suggestion) {
        cancel(false);
        closeSuggestions();
        selected = false;
        clearOutput();
        input.value = suggestion.text;
        input.dataset.addressSelected = 'false';
        input.setCustomValidity('Wait for this address to be confirmed.');
        setStatus('Confirming address...', false);
        try {
          var response = await fetch(api + '/v1/delivery/quote', {
            method: 'POST',
            headers: { 'content-type': 'application/json', 'x-trade-waste-client': client },
            body: JSON.stringify({ address: {
              address1: suggestion.text,
              address2: '',
              city: '',
              postcode: '',
              countryCode: 'NZ',
              sessionToken: ensureSession(),
              placeId: suggestion.placeId
            } })
          });
          var body = await response.json().catch(function () { return {}; });
          if (!response.ok) throw new Error(body.error && body.error.message || 'The address could not be confirmed.');
          var canonical = canonicalAddress(body);
          if (!canonical) throw new Error('Choose a complete street address from the suggestions.');
          if (full) full.value = canonical.full;
          address1.value = canonical.address1;
          suburb.value = canonical.suburb;
          city.value = canonical.city;
          postcode.value = canonical.postcode;
          if (placeId) placeId.value = canonical.placeId;
          input.value = canonical.full;
          input.dataset.addressSelected = 'true';
          input.setCustomValidity('');
          selected = true;
          setStatus('Address confirmed.', false);
          validateTouchedControl(input, false);
          refreshSubmittedSummary(form);
        } catch (error) {
          clearOutput();
          input.dataset.addressSelected = 'false';
          input.setCustomValidity('Choose and confirm a complete address from the suggestions.');
          setStatus(error && error.message ? error.message : 'The address could not be confirmed. Please try again.', true);
          validateTouchedControl(input, false);
        } finally {
          cancel(true);
        }
      }
      function renderSuggestions(items) {
        closeSuggestions();
        values = items;
        items.forEach(function (suggestion, index) {
          var option = document.createElement('li');
          option.id = input.id + '-suggestion-' + index;
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', 'false');
          var main = document.createElement('span');
          main.className = 'tw-address-suggestion__main';
          main.textContent = suggestion.mainText || suggestion.text;
          option.appendChild(main);
          if (suggestion.secondaryText) {
            var secondary = document.createElement('span');
            secondary.className = 'tw-address-suggestion__secondary';
            secondary.textContent = suggestion.secondaryText;
            option.appendChild(secondary);
          }
          var selectedByPointer = false;
          option.addEventListener('pointerdown', function (event) {
            event.preventDefault();
            selectedByPointer = true;
            confirmSuggestion(suggestion);
          });
          option.addEventListener('click', function (event) {
            event.preventDefault();
            if (selectedByPointer) return;
            confirmSuggestion(suggestion);
          });
          suggestions.appendChild(option);
        });
        panel.hidden = items.length === 0;
        input.setAttribute('aria-expanded', String(items.length > 0));
        setStatus(items.length
          ? items.length + ' address suggestion' + (items.length === 1 ? '' : 's') + ' available.'
          : 'No matching addresses found. Check the spelling and try again, or call 0800 155 255 for help.', items.length === 0);
      }
      async function loadSuggestions(query, activeSession) {
        var requestController = new AbortController();
        controller = requestController;
        input.setAttribute('aria-busy', 'true');
        setStatus('Searching for addresses...', false);
        try {
          var response = await fetch(api + '/v1/delivery/address-suggestions', {
            method: 'POST',
            headers: { 'content-type': 'application/json', 'x-trade-waste-client': client },
            body: JSON.stringify({ input: query, sessionToken: activeSession }),
            signal: requestController.signal
          });
          var body = await response.json().catch(function () { return {}; });
          if (!response.ok) throw new Error(body.error && body.error.message || 'Address suggestions are unavailable.');
          if (input.value.trim() !== query) return;
          var items = Array.isArray(body.suggestions) ? body.suggestions.filter(function (suggestion) {
            return suggestion && typeof suggestion.placeId === 'string' && suggestion.placeId
              && typeof suggestion.text === 'string' && suggestion.text;
          }).slice(0, 5) : [];
          renderSuggestions(items);
        } catch (error) {
          if (error && error.name === 'AbortError') return;
          closeSuggestions();
          setStatus(error && error.message ? error.message : 'Address suggestions are unavailable. Please try again.', true);
        } finally {
          if (controller === requestController) {
            controller = null;
            input.setAttribute('aria-busy', 'false');
          }
        }
      }
      function scheduleSuggestions() {
        cancel(false);
        closeSuggestions();
        var query = input.value.trim();
        if (query.length < 3) {
          setStatus(query ? 'Enter at least three characters to search.' : '', false);
          return;
        }
        if (!api || !client) {
          setStatus('Address lookup is unavailable. Please refresh the page and try again.', true);
          return;
        }
        var activeSession = ensureSession();
        timer = window.setTimeout(function () {
          timer = null;
          loadSuggestions(query, activeSession);
        }, 275);
      }

      input.addEventListener('input', function () {
        selected = false;
        input.dataset.addressSelected = 'false';
        input.setCustomValidity(input.value.trim() ? 'Choose a complete address from the suggestions.' : '');
        clearOutput();
        scheduleSuggestions();
        validateTouchedControl(input, false);
      });
      input.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown' && values.length) {
          event.preventDefault(); setActive(active + 1);
        } else if (event.key === 'ArrowUp' && values.length) {
          event.preventDefault(); setActive(active < 0 ? values.length - 1 : active - 1);
        } else if (event.key === 'Enter' && values.length) {
          event.preventDefault(); confirmSuggestion(values[active < 0 ? 0 : active]);
        } else if (event.key === 'Escape') {
          event.preventDefault(); closeSuggestions();
        }
      });
      input.addEventListener('blur', function () { window.setTimeout(closeSuggestions, 150); });
      form.addEventListener('submit', function (event) {
        if (selected && input.dataset.addressSelected === 'true') return;
        input.setCustomValidity('Choose and confirm a complete address from the suggestions.');
        event.preventDefault();
        event.stopImmediatePropagation();
        validateTouchedControls(form, [input], true);
        setStatus('Choose and confirm a complete address from the suggestions.', true);
      }, true);
      form.addEventListener('reset', function () {
        window.setTimeout(function () {
          selected = false;
          input.dataset.addressSelected = 'false';
          input.setCustomValidity('');
          clearOutput();
          closeSuggestions();
          setStatus('', false);
        }, 0);
      });
    });
  }

  function deliveryQuotes() {
    document.querySelectorAll('[data-delivery-quote-form]').forEach(function (form) {
      if (form.dataset.deliveryReady) return;
      form.dataset.deliveryReady = 'true';
      var api = (form.dataset.deliveryApiUrl || '').replace(/\/$/, '');
      var client = form.dataset.deliveryClientToken || '';
      var required = form.dataset.requireDeliveryQuote === 'true';
      var address = form.querySelector('[data-delivery-address]');
      var suburb = form.querySelector('[data-delivery-suburb]');
      var city = form.querySelector('[data-delivery-city]');
      var postcode = form.querySelector('[data-delivery-postcode]');
      var region = form.querySelector('[data-delivery-region]');
      var fullAddress = form.querySelector('[data-delivery-full-address]');
      var placeId = form.querySelector('[data-delivery-place-id]');
      var latitude = form.querySelector('[data-delivery-latitude]');
      var longitude = form.querySelector('[data-delivery-longitude]');
      var check = form.querySelector('[data-check-delivery]');
      var status = form.querySelector('[data-delivery-status]');
      var submit = form.querySelector('[data-add-booking], [data-form-submit]');
      var validated = form.querySelector('[data-validated-address]');
      var zone = form.querySelector('[data-delivery-zone]');
      var surcharge = form.querySelector('[data-delivery-surcharge]');
      var config = form.querySelector('[data-delivery-config]');
      var token = form.querySelector('[data-delivery-quote-token]');
      var autocomplete = form.querySelector('[data-address-autocomplete]');
      var suggestions = form.querySelector('[data-address-suggestions]');
      var suggestionsPanel = form.querySelector('[data-address-suggestions-panel]');
      var autocompleteStatus = form.querySelector('[data-address-autocomplete-status]');
      var addressSession = form.querySelector('[data-address-session-token]');
      var manual = form.querySelector('[data-address-manual]');
      var manualToggle = form.querySelector('[data-address-manual-toggle]');
      var manualMarker = form.querySelector('[data-manual-delivery-review]');
      var manualAddress = form.querySelector('[data-manual-delivery-address]');
      var manualSuburb = form.querySelector('[data-manual-delivery-suburb]');
      var manualCity = form.querySelector('[data-manual-delivery-city]');
      var manualPostcode = form.querySelector('[data-manual-delivery-postcode]');
      var manualFields = [manualAddress, manualSuburb, manualCity, manualPostcode].filter(Boolean);
      var manualRequiredFields = [manualAddress, manualSuburb, manualCity, manualPostcode].filter(Boolean);
      var suggestionTimer = null;
      var suggestionController = null;
      var suggestionValues = [];
      var activeSuggestion = -1;
      var searchSessionToken = '';
      var autocompleteSelected = false;
      var quoteVersion = 0;
      var checkLabel = check && check.textContent;
      if (!address || !postcode || !check || !status || !submit) return;

      function money(cents) { return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' }).format(cents / 100); }
      function deliveryState() {
        notifyFormState(form, 'trade-waste:delivery-state');
      }
      function manualModeEnabled() {
        return Boolean(manualToggle && manualToggle.checked);
      }
      function clearQuote() {
        quoteVersion += 1;
        [validated, zone, surcharge, config, token].forEach(function (field) { if (field) field.value = ''; });
        if (fullAddress) fullAddress.value = '';
        status.className = 'tw-delivery-status';
        status.textContent = required ? 'Check the address to confirm delivery availability and the charge.' : '';
        if (required) submit.disabled = true;
        check.disabled = false;
        deliveryState();
      }
      function message(text, type) {
        status.className = 'tw-delivery-status tw-delivery-status--' + type;
        status.textContent = text;
      }
      function autocompleteMessage(text, error) {
        if (!autocompleteStatus) return;
        autocompleteStatus.textContent = text || '';
        autocompleteStatus.classList.toggle('tw-address-autocomplete__status--error', Boolean(error));
      }
      function clearCanonicalAddress() {
        [address, suburb, city, postcode, region, placeId, latitude, longitude].forEach(function (field) {
          if (field) field.value = '';
        });
        if (fullAddress) fullAddress.value = '';
      }
      function closeSuggestions() {
        suggestionValues = [];
        activeSuggestion = -1;
        if (suggestions) {
          suggestions.replaceChildren();
        }
        if (suggestionsPanel) suggestionsPanel.hidden = true;
        if (autocomplete) {
          autocomplete.setAttribute('aria-expanded', 'false');
          autocomplete.removeAttribute('aria-activedescendant');
        }
      }
      function cancelSuggestionRequest(resetSession) {
        if (suggestionTimer) window.clearTimeout(suggestionTimer);
        suggestionTimer = null;
        if (suggestionController) suggestionController.abort();
        suggestionController = null;
        if (autocomplete) autocomplete.setAttribute('aria-busy', 'false');
        if (resetSession) {
          searchSessionToken = '';
          if (addressSession) addressSession.value = '';
        }
      }
      function ensureSearchSession() {
        if (!searchSessionToken) {
          searchSessionToken = idempotencyKey().replace(/[^A-Za-z0-9_-]/g, '').slice(0, 36);
          if (addressSession) addressSession.value = searchSessionToken;
        }
        return searchSessionToken;
      }
      function setActiveSuggestion(index) {
        if (!suggestions || !suggestionValues.length) return;
        var options = Array.from(suggestions.querySelectorAll('[role="option"]'));
        activeSuggestion = (index + options.length) % options.length;
        options.forEach(function (option, position) {
          option.setAttribute('aria-selected', String(position === activeSuggestion));
        });
        var active = options[activeSuggestion];
        if (active && autocomplete) {
          autocomplete.setAttribute('aria-activedescendant', active.id);
          if (typeof active.scrollIntoView === 'function') active.scrollIntoView({ block: 'nearest' });
        }
      }
      function structuredAddress(body) {
        var value = body && body.structuredAddress;
        var location = body && body.location;
        var responsePlaceId = body && typeof body.placeId === 'string' && body.placeId.trim()
          ? body.placeId.trim()
          : (placeId && placeId.value ? placeId.value.trim() : '');
        var valid = value && typeof value.address1 === 'string' && value.address1.trim().length >= 4
          && typeof value.city === 'string' && value.city.trim()
          && typeof value.region === 'string' && value.region.trim()
          && typeof value.postcode === 'string' && /^\d{4}$/.test(value.postcode.trim())
          && location && Number.isFinite(Number(location.latitude)) && Number.isFinite(Number(location.longitude));
        if (!valid) return null;
        return {
          address1: value.address1.trim(),
          suburb: typeof value.suburb === 'string' ? value.suburb.trim() : '',
          city: value.city.trim(),
          region: value.region.trim(),
          postcode: value.postcode.trim(),
          placeId: responsePlaceId,
          latitude: String(location.latitude),
          longitude: String(location.longitude)
        };
      }
      function applyStructuredAddress(body) {
        var structured = structuredAddress(body);
        if (!structured) {
          if (autocomplete) throw new Error('The validated address was incomplete. Please choose the address again.');
          return;
        }
        address.value = structured.address1;
        if (suburb) suburb.value = structured.suburb;
        if (city) city.value = structured.city;
        postcode.value = structured.postcode;
        if (region) region.value = structured.region;
        if (placeId && structured.placeId) placeId.value = structured.placeId;
        if (latitude) latitude.value = structured.latitude;
        if (longitude) longitude.value = structured.longitude;
        if (fullAddress) fullAddress.value = body.validatedAddress || [structured.address1, structured.suburb, structured.city, structured.postcode].filter(Boolean).join(', ');
        if (manualModeEnabled()) {
          if (manualAddress) manualAddress.value = structured.address1;
          if (manualSuburb) manualSuburb.value = structured.suburb;
          if (manualCity) manualCity.value = structured.city;
          if (manualPostcode) manualPostcode.value = structured.postcode;
        }
        if (autocomplete) {
          autocomplete.value = body.validatedAddress || (fullAddress && fullAddress.value) || structured.address1;
          autocomplete.setCustomValidity('');
          autocomplete.dataset.addressSelected = 'true';
          autocompleteSelected = true;
          validateTouchedControl(autocomplete, false);
        }
      }
      function quoteAddress(sessionToken) {
        return {
          address1: address.value,
          address2: suburb ? suburb.value : '',
          city: city ? city.value : '',
          postcode: postcode.value,
          countryCode: 'NZ',
          ...(sessionToken ? { sessionToken: sessionToken } : {}),
          ...(placeId && placeId.value ? { placeId: placeId.value.trim() } : {})
        };
      }
      async function requestQuote(requestAddress, retireAutocompleteSession) {
        if (!api || !client) {
          message('Online delivery checking is temporarily unavailable. Please contact Trade Waste before booking.', 'error');
          return false;
        }
        var completedSessionToken = retireAutocompleteSession && requestAddress ? requestAddress.sessionToken : '';
        var requestVersion = ++quoteVersion;
        check.disabled = true;
        message('Checking this address...', 'working');
        try {
          var response = await fetch(api + '/v1/delivery/quote', {
            method: 'POST',
            headers: { 'content-type': 'application/json', 'x-trade-waste-client': client },
            body: JSON.stringify({ address: requestAddress || quoteAddress('') })
          });
          var body = await response.json();
          if (requestVersion !== quoteVersion) return false;
          if (!response.ok) throw new Error(body.error && body.error.message || 'Address check failed.');
          if (!body.serviceable) {
            clearQuote();
            message(body.reasonCode === 'EXCLUDED_AREA' ? 'Delivery is not available to this address. Banks Peninsula and other excluded areas are outside our service.' : 'This address is outside our standard delivery area. Please contact us to discuss it.', 'error');
            autocompleteMessage('', false);
            return false;
          }
          applyStructuredAddress(body);
          if (validated) validated.value = body.validatedAddress || [address.value, suburb && suburb.value, city && city.value, postcode.value].filter(Boolean).join(', ');
          if (fullAddress) fullAddress.value = body.validatedAddress || validated.value;
          if (zone) zone.value = body.zoneId || '';
          if (surcharge) surcharge.value = String(body.surchargeCents || 0);
          if (config) config.value = body.configVersion || '';
          if (token) token.value = body.quoteToken || '';
          var bandMatch = String(body.zoneId || '').match(/^band[_-](\d+)$/i);
          var bandName = bandMatch ? 'Band ' + bandMatch[1] : 'Delivery';
          var text = body.surchargeCents === 0
            ? bandName + ': delivery is included for this address.'
            : bandName + ' delivery charge: ' + money(body.surchargeCents) + ' inc GST.';
          if (body.addressQuality && body.addressQuality.confirmationRequired && body.validatedAddress) text += ' We matched the address as ' + body.validatedAddress + '; please check it carefully.';
          message(text, 'success');
          autocompleteMessage('', false);
          deliveryState();
          return true;
        } catch (error) {
          if (requestVersion !== quoteVersion) return false;
          clearQuote();
          message(error && error.message ? error.message : 'We could not check the address. Please try again.', 'error');
          autocompleteMessage('', false);
          deliveryState();
          return false;
        } finally {
          if (completedSessionToken && searchSessionToken === completedSessionToken) {
            searchSessionToken = '';
            if (addressSession) addressSession.value = '';
          }
          if (requestVersion === quoteVersion) check.disabled = false;
        }
      }
      function selectSuggestion(suggestion) {
        if (!autocomplete || !suggestion) return;
        cancelSuggestionRequest(false);
        closeSuggestions();
        clearQuote();
        clearCanonicalAddress();
        autocomplete.value = suggestion.text;
        autocomplete.setCustomValidity('');
        autocomplete.dataset.addressSelected = 'true';
        autocompleteSelected = true;
        address.value = suggestion.text;
        if (placeId) placeId.value = suggestion.placeId;
        autocompleteMessage('Address selected. Checking delivery availability and the charge.', false);
        validateTouchedControl(autocomplete, false);
        requestQuote({
          address1: suggestion.text,
          address2: '',
          city: '',
          postcode: '',
          countryCode: 'NZ',
          sessionToken: ensureSearchSession(),
          placeId: suggestion.placeId
        }, true);
      }
      function renderSuggestions(values) {
        if (!autocomplete || !suggestions) return;
        closeSuggestions();
        suggestionValues = values;
        values.forEach(function (suggestion, index) {
          var option = document.createElement('li');
          option.id = autocomplete.id + '-suggestion-' + index;
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', 'false');
          var main = document.createElement('span');
          main.className = 'tw-address-suggestion__main';
          main.textContent = suggestion.mainText || suggestion.text;
          option.appendChild(main);
          if (suggestion.secondaryText) {
            var secondary = document.createElement('span');
            secondary.className = 'tw-address-suggestion__secondary';
            secondary.textContent = suggestion.secondaryText;
            option.appendChild(secondary);
          }
          var selectedByPointer = false;
          option.addEventListener('pointerdown', function (event) {
            event.preventDefault();
            selectedByPointer = true;
            selectSuggestion(suggestion);
          });
          option.addEventListener('click', function (event) {
            event.preventDefault();
            if (selectedByPointer) return;
            selectSuggestion(suggestion);
          });
          suggestions.appendChild(option);
        });
        if (suggestionsPanel) suggestionsPanel.hidden = values.length === 0;
        autocomplete.setAttribute('aria-expanded', String(values.length > 0));
        if (values.length) {
          autocompleteMessage(values.length + ' address suggestion' + (values.length === 1 ? '' : 's') + ' available.', false);
        } else {
          autocompleteMessage('No matching addresses found. Check the spelling and try again, or call 0800 155 255 for help.', true);
        }
      }
      async function loadSuggestions(query, sessionToken) {
        if (!autocomplete || !suggestions) return;
        var controller = new AbortController();
        suggestionController = controller;
        autocomplete.setAttribute('aria-busy', 'true');
        autocompleteMessage('Searching for addresses...', false);
        try {
          var response = await fetch(api + '/v1/delivery/address-suggestions', {
            method: 'POST',
            headers: { 'content-type': 'application/json', 'x-trade-waste-client': client },
            body: JSON.stringify({ input: query, sessionToken: sessionToken }),
            signal: controller.signal
          });
          var body = await response.json().catch(function () { return {}; });
          if (!response.ok) throw new Error(body.error && body.error.message || 'Address suggestions are temporarily unavailable.');
          if (!autocomplete || autocomplete.value.trim() !== query || manualModeEnabled()) return;
          var values = Array.isArray(body.suggestions) ? body.suggestions.filter(function (suggestion) {
            return suggestion && typeof suggestion.placeId === 'string' && suggestion.placeId
              && typeof suggestion.text === 'string' && suggestion.text;
          }).slice(0, 5) : [];
          renderSuggestions(values);
        } catch (error) {
          if (error && error.name === 'AbortError') return;
          closeSuggestions();
          autocompleteMessage((error && error.message ? error.message : 'Address suggestions are temporarily unavailable. Please try again.') + ' Call 0800 155 255 if you need help.', true);
        } finally {
          if (suggestionController === controller) {
            if (autocomplete) autocomplete.setAttribute('aria-busy', 'false');
            suggestionController = null;
          }
        }
      }
      function scheduleSuggestions() {
        if (!autocomplete) return;
        cancelSuggestionRequest(false);
        closeSuggestions();
        var query = autocomplete.value.trim();
        if (query.length < 3) {
          autocompleteMessage(query ? 'Enter at least three characters to search.' : '', false);
          return;
        }
        if (!api || !client) {
          autocompleteMessage('Address suggestions are temporarily unavailable. Please try again or call 0800 155 255 for help.', true);
          return;
        }
        var sessionToken = ensureSearchSession();
        suggestionTimer = window.setTimeout(function () {
          suggestionTimer = null;
          loadSuggestions(query, sessionToken);
        }, 275);
      }
      function syncManualAddress() {
        address.value = manualAddress ? manualAddress.value.trim() : '';
        if (suburb) suburb.value = manualSuburb ? manualSuburb.value.trim() : '';
        if (city) city.value = manualCity ? manualCity.value.trim() : '';
        postcode.value = manualPostcode ? manualPostcode.value.trim() : '';
        if (region) region.value = 'Canterbury';
      }
      function confirmManualAddress() {
        syncManualAddress();
        var formatted = [address.value, suburb && suburb.value, city && city.value, postcode.value].filter(Boolean).join(', ');
        if (fullAddress) fullAddress.value = formatted;
        if (validated) validated.value = formatted;
        if (zone) zone.value = 'manual_review';
        if (surcharge) surcharge.value = '0';
        if (config) config.value = 'manual-review';
        if (token) token.value = 'manual-delivery-review';
        if (manualMarker) manualMarker.value = 'Yes';
        message('The delivery charge cannot be automatically calculated for a manual address entry, we will contact you after booking to discuss delivery.', 'success');
        autocompleteMessage('Manual address entry is active.', false);
        deliveryState();
      }
      function setManualMode(enabled) {
        if (!autocomplete || !manual || !manualFields.length) return;
        var moveFocusToManualAddress = document.activeElement === autocomplete;
        closeSuggestions();
        cancelSuggestionRequest(enabled);
        if (enabled) {
          if (address.value && manualAddress) manualAddress.value = address.value;
          if (suburb && suburb.value && manualSuburb) manualSuburb.value = suburb.value;
          if (city && city.value && manualCity) manualCity.value = city.value;
          if (postcode.value && manualPostcode) manualPostcode.value = postcode.value;
          autocomplete.required = false;
          autocomplete.disabled = true;
          autocomplete.setCustomValidity('');
          manual.hidden = false;
          if (manualMarker) manualMarker.value = 'Yes';
          check.textContent = 'Continue with manual address';
          manualFields.forEach(function (field) {
            field.disabled = false;
            field.required = manualRequiredFields.includes(field);
          });
          autocompleteMessage('Manual address entry is active.', false);
          if (moveFocusToManualAddress && manualAddress) manualAddress.focus();
          clearQuote();
          if (manualMarker) manualMarker.value = 'Yes';
          return;
        }
        manual.hidden = true;
        if (manualMarker) manualMarker.value = '';
        check.textContent = checkLabel;
        manualFields.forEach(function (field) { field.required = false; field.disabled = true; });
        autocomplete.disabled = false;
        autocomplete.required = true;
        autocomplete.value = '';
        autocomplete.dataset.addressSelected = 'false';
        autocompleteSelected = false;
        autocomplete.setCustomValidity('');
        clearCanonicalAddress();
        clearQuote();
        autocompleteMessage('', false);
        validateTouchedControl(autocomplete, false);
      }

      if (autocomplete && suggestions) {
        autocomplete.addEventListener('input', function () {
          var hadSelection = autocompleteSelected;
          autocompleteSelected = false;
          autocomplete.dataset.addressSelected = 'false';
          autocomplete.setCustomValidity(autocomplete.value.trim() ? 'Choose a delivery address from the suggestions.' : '');
          if (hadSelection) cancelSuggestionRequest(true);
          clearCanonicalAddress();
          clearQuote();
          scheduleSuggestions();
          validateTouchedControl(autocomplete, false);
        });
        autocomplete.addEventListener('keydown', function (event) {
          if (event.key === 'ArrowDown' && suggestionValues.length) {
            event.preventDefault();
            setActiveSuggestion(activeSuggestion + 1);
          } else if (event.key === 'ArrowUp' && suggestionValues.length) {
            event.preventDefault();
            setActiveSuggestion(activeSuggestion < 0 ? suggestionValues.length - 1 : activeSuggestion - 1);
          } else if (event.key === 'Enter' && suggestionValues.length) {
            event.preventDefault();
            selectSuggestion(suggestionValues[activeSuggestion < 0 ? 0 : activeSuggestion]);
          } else if (event.key === 'Escape') {
            event.preventDefault();
            closeSuggestions();
          }
        });
        autocomplete.addEventListener('blur', function () {
          window.setTimeout(closeSuggestions, 150);
        });
        if (manualToggle) manualToggle.addEventListener('change', function () { setManualMode(manualToggle.checked); });
        manualFields.forEach(function (field) {
          field.addEventListener('input', function () {
            cancelSuggestionRequest(true);
            closeSuggestions();
            autocomplete.value = '';
            autocompleteSelected = false;
            autocomplete.dataset.addressSelected = 'false';
            clearCanonicalAddress();
            syncManualAddress();
            clearQuote();
          });
        });
      } else {
        [address, suburb, city, postcode].forEach(function (field) { if (field) field.addEventListener('input', clearQuote); });
      }

      check.addEventListener('click', async function () {
        if (autocomplete && manualModeEnabled()) {
          if (!validateTouchedControls(form, manualFields, true)) return;
          confirmManualAddress();
          return;
        }
        if (autocomplete) {
          if (!autocompleteSelected) {
            autocomplete.setCustomValidity('Choose a delivery address from the suggestions.');
            validateTouchedControls(form, [autocomplete], true);
            message('Choose a complete delivery address from the suggestions.', 'error');
            autocomplete.focus();
            return;
          }
          await requestQuote(quoteAddress(searchSessionToken));
          return;
        }
        if (!validateTouchedControls(form, [address, suburb, city, postcode], true)) return;
        await requestQuote(quoteAddress(''));
      });
      form.addEventListener('submit', function (event) {
        if (required && (!token || !token.value)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          message('Check the delivery address before continuing.', 'error');
          check.focus();
        }
      });
      clearQuote();
      if (manualToggle && manualToggle.checked) setManualMode(true);
    });
  }

  function normaliseAddress(value) { return (value || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
  function normaliseCheckoutPhone(value) {
    var phone = String(value || '').trim().replace(/[\s().-]/g, '');
    if (phone.charAt(0) === '0') phone = '+64' + phone.slice(1);
    else if (phone.slice(0, 2) === '64') phone = '+' + phone;
    if (!/^\+[1-9]\d{7,14}$/.test(phone)) throw new Error('Enter a valid mobile phone number, including the country code if it is not a New Zealand number.');
    return phone;
  }
  function propertiesFromForm(form) {
    var properties = {};
    new FormData(form).forEach(function (value, key) {
      var match = key.match(/^properties\[(.+)\]$/);
      if (match && String(value).trim() !== '') properties[match[1]] = String(value);
    });
    return properties;
  }
  function checkoutAddressFromCart(cart) {
    var bookingItems = (cart.items || []).filter(function (item) {
      return item.properties && item.properties._booking_key;
    });
    if (!bookingItems.length) return null;
    var bookingItem = bookingItems.find(function (item) {
      var properties = item.properties || {};
      return Boolean(
        properties._validated_delivery_address
        && properties._delivery_quote_token
        && (properties._delivery_address1 || properties['Delivery street address'])
        && (properties._delivery_city || properties['Delivery city'] || properties['Delivery city or district'])
        && (properties._delivery_postcode || properties['Delivery postcode'])
      );
    });
    if (!bookingItem) throw new Error('The confirmed delivery address is missing from the booking item.');
    var properties = bookingItem.properties || {};
    var address1 = String(properties._delivery_address1 || properties['Delivery street address'] || '').trim();
    var address2 = String(properties._delivery_suburb || properties['Delivery suburb'] || '').trim();
    var city = String(properties._delivery_city || properties['Delivery city'] || properties['Delivery city or district'] || '').trim();
    var postcode = String(properties._delivery_postcode || properties['Delivery postcode'] || '').trim();
    var region = String(properties._delivery_region || 'Canterbury').trim();
    var firstName = String(properties._delivery_first_name || '').trim();
    var lastName = String(properties._delivery_last_name || '').trim();
    var rawPhone = String(properties._delivery_phone || '').trim();
    if (!address1 || !city || !postcode) throw new Error('The confirmed delivery address is incomplete.');
    if (!firstName || !lastName || !rawPhone) throw new Error('The delivery contact details are missing. Remove this booking and add it again with the required contact details.');
    var phone = normaliseCheckoutPhone(rawPhone);

    var nzProvinceCodes = {
      'auckland': 'AUK',
      'bay of plenty': 'BOP',
      'canterbury': 'CAN',
      'chatham islands': 'CIT',
      'gisborne': 'GIS',
      "hawke's bay": 'HKB',
      'hawkes bay': 'HKB',
      'manawatu-whanganui': 'MWT',
      'manawatu wanganui': 'MWT',
      'marlborough': 'MBH',
      'nelson': 'NSN',
      'northland': 'NTL',
      'otago': 'OTA',
      'southland': 'STL',
      'taranaki': 'TKI',
      'tasman': 'TAS',
      'waikato': 'WKO',
      'wellington': 'WGN',
      'west coast': 'WTC'
    };
    var deliveryAddress = {
      address1: address1,
      city: city,
      countryCode: 'NZ',
      firstName: firstName,
      lastName: lastName,
      phone: phone,
      zip: postcode
    };
    if (address2) deliveryAddress.address2 = address2;
    if (nzProvinceCodes[region.toLowerCase()]) deliveryAddress.provinceCode = nzProvinceCodes[region.toLowerCase()];
    return deliveryAddress;
  }

  function manualDeliveryReviewFromCart(cart) {
    return (cart.items || []).some(function (item) {
      return item.properties && item.properties._booking_key
        && item.properties._manual_delivery_review === 'Yes';
    });
  }

  function storefrontLineInput(item) {
    var line = {
      merchandiseId: 'gid://shopify/ProductVariant/' + String(item.variant_id),
      quantity: Math.max(1, Number(item.quantity) || 1)
    };
    var attributes = Object.keys(item.properties || {}).filter(function (key) {
      var value = item.properties[key];
      return key !== '_delivery_quote_token'
        && value !== null && value !== undefined && String(value) !== '';
    }).map(function (key) {
      return { key: key, value: String(item.properties[key]) };
    });
    if (attributes.length) line.attributes = attributes;
    var sellingPlanId = item.selling_plan_allocation
      && item.selling_plan_allocation.selling_plan
      && item.selling_plan_allocation.selling_plan.id;
    if (sellingPlanId) {
      line.sellingPlanId = String(sellingPlanId).indexOf('gid://') === 0
        ? String(sellingPlanId)
        : 'gid://shopify/SellingPlan/' + String(sellingPlanId);
    }
    return line;
  }

  function checkoutPreferenceFromDeliveryAddress(deliveryAddress) {
    var nzProvinceNames = {
      AUK: 'Auckland', BOP: 'Bay of Plenty', CAN: 'Canterbury', CIT: 'Chatham Islands',
      GIS: 'Gisborne', HKB: "Hawke's Bay", MWT: 'Manawatu-Whanganui', MBH: 'Marlborough',
      NSN: 'Nelson', NTL: 'Northland', OTA: 'Otago', STL: 'Southland', TKI: 'Taranaki',
      TAS: 'Tasman', WKO: 'Waikato', WGN: 'Wellington', WTC: 'West Coast'
    };
    var preference = {
      address1: deliveryAddress.address1,
      city: deliveryAddress.city,
      country: 'New Zealand',
      firstName: deliveryAddress.firstName,
      lastName: deliveryAddress.lastName,
      phone: deliveryAddress.phone,
      zip: deliveryAddress.zip
    };
    if (deliveryAddress.address2) preference.address2 = deliveryAddress.address2;
    if (nzProvinceNames[deliveryAddress.provinceCode]) preference.province = nzProvinceNames[deliveryAddress.provinceCode];
    return preference;
  }

  function storefrontCheckoutFailure(response, body, payload) {
    var issues = [];
    var graphqlErrors = body && body.errors;
    if (Array.isArray(graphqlErrors)) {
      graphqlErrors.forEach(function (error) {
        issues.push({
          code: error && error.extensions && error.extensions.code || 'GRAPHQL_ERROR',
          field: null,
          message: error && error.message || 'Shopify returned a GraphQL error.'
        });
      });
    } else if (graphqlErrors) {
      issues.push({
        code: 'STOREFRONT_HTTP_' + String(response && response.status || 'ERROR'),
        field: null,
        message: typeof graphqlErrors === 'string' ? graphqlErrors : 'Shopify returned an API error.'
      });
    }
    if (payload && Array.isArray(payload.userErrors)) {
      payload.userErrors.forEach(function (error) {
        issues.push({
          code: error && error.code || 'CART_USER_ERROR',
          field: error && error.field || null,
          message: error && error.message || 'Shopify rejected a cart value.'
        });
      });
    }
    var failure = new Error(issues.length
      ? issues.map(function (issue) { return issue.code + ': ' + issue.message; }).join('; ')
      : 'Shopify did not return a checkout URL.');
    failure.checkoutStatus = response && response.status || null;
    failure.checkoutIssues = issues;
    return failure;
  }

  function showCheckoutHandoffError(form, error) {
    var summary = ensureValidationSummary(form);
    summary.replaceChildren();
    var heading = document.createElement('h2');
    heading.className = 'tw-validation-summary__title';
    heading.textContent = 'Checkout could not be opened';
    var detail = document.createElement('p');
    detail.textContent = 'We could not transfer the confirmed delivery address to checkout. Please try again.';
    var issue = error && Array.isArray(error.checkoutIssues) && error.checkoutIssues[0];
    if (issue && issue.code) detail.textContent += ' Shopify reference: ' + issue.code + '.';
    summary.append(heading, detail);
    summary.hidden = false;
    summary.focus();
    summary.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
  }

  function cartCheckoutHandoff() {
    document.querySelectorAll('[data-cart-form]').forEach(function (form) {
      if (form.dataset.checkoutHandoffReady === 'true') return;
      form.dataset.checkoutHandoffReady = 'true';
      form.addEventListener('submit', async function (event) {
        var submitter = event.submitter;
        if (!submitter || submitter.name !== 'checkout') return;
        event.preventDefault();
        event.stopImmediatePropagation();

        var originalText = submitter.textContent;
        var redirecting = false;
        submitter.disabled = true;
        submitter.textContent = 'Preparing secure checkout...';
        try {
          var updateData = new FormData(form);
          Array.from(updateData.keys()).forEach(function (key) {
            if (key === 'checkout' || key.indexOf('checkout[') === 0) updateData.delete(key);
          });
          var cartRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root
            : '/';
          if (cartRoot.charAt(cartRoot.length - 1) !== '/') cartRoot += '/';
          var cartResponse = await fetch(cartRoot + 'cart/update.js', {
            method: 'POST',
            headers: { accept: 'application/json' },
            credentials: 'same-origin',
            body: updateData
          });
          var cart = await cartResponse.json();
          if (!cartResponse.ok || !cart || !Array.isArray(cart.items)) throw new Error('The cart could not be read.');

          var deliveryAddress = checkoutAddressFromCart(cart);
          if (!deliveryAddress) {
            redirecting = true;
            window.location.assign(cartRoot + 'checkout');
            return;
          }
          var addressValidationStrategy = manualDeliveryReviewFromCart(cart) ? 'COUNTRY_CODE_ONLY' : 'STRICT';

          var cartAttributes = Object.keys(cart.attributes || {}).filter(function (key) {
            var value = cart.attributes[key];
            return value !== null && value !== undefined && String(value) !== '';
          }).map(function (key) {
            return { key: key, value: String(cart.attributes[key]) };
          });
          var cartInput = {
            lines: cart.items.map(storefrontLineInput),
            buyerIdentity: {
              countryCode: 'NZ',
              deliveryAddressPreferences: [{
                deliveryAddress: checkoutPreferenceFromDeliveryAddress(deliveryAddress),
                deliveryAddressValidationStrategy: addressValidationStrategy,
                oneTimeUse: true
              }]
            },
            delivery: {
              addresses: [{
                address: { deliveryAddress: deliveryAddress },
                oneTimeUse: true,
                selected: true,
                validationStrategy: addressValidationStrategy
              }]
            }
          };
          if (cartAttributes.length) cartInput.attributes = cartAttributes;
          if (cart.note) cartInput.note = String(cart.note);

          var storefrontResponse = await fetch('/api/2026-07/graphql.json', {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'content-type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
              query: 'mutation TradeWasteCheckout($input: CartInput!) { cartCreate(input: $input) { cart { checkoutUrl delivery { addresses { selected address { ... on CartDeliveryAddress { address1 address2 city provinceCode countryCode zip } } } } } userErrors { code field message } warnings { message } } }',
              variables: { input: cartInput }
            })
          });
          var storefrontBody = await storefrontResponse.json();
          var payload = storefrontBody
            && storefrontBody.data
            && storefrontBody.data.cartCreate;
          if (!storefrontResponse.ok
            || (storefrontBody && storefrontBody.errors && (Array.isArray(storefrontBody.errors)
              ? storefrontBody.errors.length : true))
            || !payload
            || (payload.userErrors && payload.userErrors.length)
            || !payload.cart
            || !payload.cart.checkoutUrl) {
            throw storefrontCheckoutFailure(storefrontResponse, storefrontBody, payload);
          }
          var selectedAddress = payload.cart.delivery
            && Array.isArray(payload.cart.delivery.addresses)
            && payload.cart.delivery.addresses.find(function (address) { return address.selected; });
          var selectedDeliveryAddress = selectedAddress && selectedAddress.address;
          if (!selectedDeliveryAddress
            || normaliseAddress(selectedDeliveryAddress.address1) !== normaliseAddress(deliveryAddress.address1)
            || normaliseAddress(selectedDeliveryAddress.city) !== normaliseAddress(deliveryAddress.city)
            || normaliseAddress(selectedDeliveryAddress.zip) !== normaliseAddress(deliveryAddress.zip)
            || String(selectedDeliveryAddress.countryCode || '').toUpperCase() !== 'NZ') {
            throw new Error('Shopify did not select the confirmed delivery address for checkout.');
          }
          redirecting = true;
          window.location.assign(payload.cart.checkoutUrl);
        } catch (error) {
          if (window.console && typeof window.console.error === 'function') {
            window.console.error('Trade Waste checkout handoff failed', error);
          }
          showCheckoutHandoffError(form, error);
        } finally {
          if (!redirecting) {
            submitter.disabled = false;
            submitter.textContent = originalText;
          }
        }
      });
    });
  }

  function bookingForms() {
    document.querySelectorAll('[data-booking-form]').forEach(function (form) {
      if (form.dataset.bookingReady) return;
      form.dataset.bookingReady = 'true';
      var submit = form.querySelector('[data-add-booking]');
      var errors = form.querySelector('[data-booking-errors]');
      var readiness = form.querySelector('[data-booking-readiness]');
      if (!submit || !errors || !readiness) return;

      function selectedVariantAvailable() {
        var variant = form.querySelector('[name="id"]');
        if (!variant || !variant.value) return false;
        if (variant.tagName && variant.tagName.toLowerCase() === 'select') {
          var option = variant.options[variant.selectedIndex];
          return Boolean(option && !option.disabled);
        }
        return !variant.disabled;
      }

      function readinessMessage() {
        if (!selectedVariantAvailable()) return 'Choose an available hire option. If every option is unavailable, please contact Trade Waste.';
        var invalid = uniqueValidationControls(form).find(function (control) {
          return !unavailableControl(control) && !control.checkValidity();
        });
        if (invalid) return validationMessage(invalid);
        var quoteToken = form.querySelector('[data-delivery-quote-token]');
        if (form.dataset.requireDeliveryQuote === 'true' && (!quoteToken || !quoteToken.value)) {
          return 'Select and check the delivery address to confirm availability and the delivery charge.';
        }
        return '';
      }

      function updateReadiness() {
        if (form.dataset.bookingSubmitting === 'true') return;
        var message = readinessMessage();
        submit.disabled = Boolean(message);
        readiness.textContent = message || 'Booking details complete. You can add this booking to the cart.';
        readiness.classList.toggle('tw-booking-readiness--ready', !message);
      }

      function showError(message) {
        errors.textContent = message;
        if (/^All bins in one checkout must use the same delivery address\./.test(message)) {
          var cartLink = document.createElement('a');
          cartLink.href = '/cart';
          cartLink.textContent = ' View your cart.';
          errors.appendChild(cartLink);
        }
        errors.hidden = false;
        errors.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      }
      form.addEventListener('input', updateReadiness);
      form.addEventListener('change', updateReadiness);
      form.addEventListener('trade-waste:delivery-state', updateReadiness);
      form.addEventListener('trade-waste:terms-state', updateReadiness);
      form.addEventListener('trade-waste:availability-state', updateReadiness);
      form.addEventListener('submit', async function (event) {
        event.preventDefault();
        errors.hidden = true;
        if (!validateTouchedForm(form, true)) return;
        var quoteToken = form.querySelector('[data-delivery-quote-token]');
        if (form.dataset.requireDeliveryQuote === 'true' && (!quoteToken || !quoteToken.value)) {
          showError('Check the delivery address before adding this booking.');
          return;
        }
        var variant = form.querySelector('[name="id"]');
        var mainQuantity = Number(form.querySelector('[data-main-quantity]') && form.querySelector('[data-main-quantity]').value) || 1;
        if (!variant || !variant.value) { showError('Choose an available hire option.'); return; }
        var acceptedAt = new Date().toISOString();
        var firstName = form.querySelector('[data-delivery-first-name]');
        var lastName = form.querySelector('[data-delivery-last-name]');
        var phone = form.querySelector('[data-delivery-phone]');
        var acceptedBy = form.querySelector('[data-terms-accepted-by]');
        try {
          phone.value = normaliseCheckoutPhone(phone.value);
        } catch (error) {
          showError(error.message);
          phone.focus();
          return;
        }
        acceptedBy.value = [firstName.value.trim(), lastName.value.trim()].filter(Boolean).join(' ');
        var acceptedAtField = form.querySelector('[data-terms-accepted-at]');
        if (acceptedAtField) acceptedAtField.value = acceptedAt;
        var bookingKey = form.dataset.idempotencyKey || idempotencyKey();
        form.dataset.idempotencyKey = bookingKey;
        var keyField = form.querySelector('[data-booking-key]');
        if (keyField) keyField.value = bookingKey;
        var properties = propertiesFromForm(form);
        properties._booking_key = bookingKey;
        var deliveryAddress1 = properties._delivery_address1 || properties['Delivery street address'];
        var deliverySuburb = properties._delivery_suburb || properties['Delivery suburb'];
        var deliveryCity = properties._delivery_city || properties['Delivery city'] || properties['Delivery city or district'];
        var deliveryPostcode = properties._delivery_postcode || properties['Delivery postcode'];
        var currentAddress = properties._validated_delivery_address || properties['Delivery address'] || [deliveryAddress1, deliverySuburb, deliveryCity, deliveryPostcode].filter(Boolean).join(', ');

        form.dataset.bookingSubmitting = 'true';
        submit.disabled = true;
        submit.textContent = 'Preparing booking...';
        try {
          var existingResponse = await fetch('/cart.js', { headers: { accept: 'application/json' } });
          var existingCart = await existingResponse.json();
          var differentAddress = existingCart.items && existingCart.items.some(function (item) {
            if (item.properties && item.properties._booking_key && item.properties._validated_delivery_address) {
              return normaliseAddress(item.properties._validated_delivery_address) !== normaliseAddress(currentAddress);
            }
            return false;
          });
          if (differentAddress) throw new Error('All bins in one checkout must use the same delivery address. Complete the existing checkout first, or remove it before booking another address.');

          var items = [{ id: Number(variant.value), quantity: mainQuantity, properties: properties }];
          form.querySelectorAll('[data-addon-quantity]').forEach(function (input) {
            var quantity = Number(input.value) || 0;
            if (input.dataset.multiplyByMain === 'true') quantity *= mainQuantity;
            if (quantity > 0) {
              if (!input.dataset.addonId) throw new Error(input.dataset.addonName + ' is temporarily unavailable. Please contact Trade Waste.');
              items.push({
                id: Number(input.dataset.addonId),
                quantity: quantity,
                properties: { 'For booking': form.dataset.productTitle, '_booking_key': bookingKey }
              });
            }
          });
          var preparation = await bookingPreparationRequest(form, {
            items: items.map(function (item) {
              var preparationProperties = Object.assign({}, item.properties);
              delete preparationProperties._delivery_quote_token;
              return { variantId: String(item.id), quantity: item.quantity, properties: preparationProperties };
            }),
            deliveryAddress: {
              address1: deliveryAddress1,
              address2: deliverySuburb,
              city: deliveryCity,
              postcode: deliveryPostcode,
              countryCode: 'NZ',
              ...(properties._delivery_place_id ? { placeId: properties._delivery_place_id } : {})
            },
            deliveryQuoteToken: quoteToken ? quoteToken.value : '',
            manualDeliveryReview: properties._manual_delivery_review === 'Yes',
            requestedDeliveryDate: properties['Requested delivery date'],
            deliveryWindow: properties['Preferred delivery window'],
            access: {
              gates: properties['Access - gates'] === 'Yes',
              narrowDriveway: properties['Access - narrow driveway'] === 'Yes',
              overheadWires: properties['Access - overhead wires'] === 'Yes',
              softGround: properties['Access - soft ground'] === 'Yes',
              roadPlacement: properties['Placement - public land'] === 'Yes',
              permitConfirmed: properties['Placement and permit responsibility accepted'] === 'Yes'
            },
            placementInstructions: properties['Placement and access instructions'],
            typedName: properties['Terms accepted by'],
            termsVersion: form.dataset.termsVersion,
            termsAccepted: true,
            termsAcceptedAt: acceptedAt,
            submittedAt: acceptedAt
          });
          properties._booking_key = preparation.reference;
          properties._operations_reference = preparation.reference;
          properties._reservation_id = preparation.reservationId || '';
          properties._reservation_expires_at = preparation.expiresAt || '';
          properties._terms_version = form.dataset.termsVersion;
          properties._terms_accepted_at = acceptedAt;
          items[0].properties = properties;
          items.slice(1).forEach(function (item) {
            item.properties._booking_key = preparation.reference;
            item.properties._operations_reference = preparation.reference;
            item.properties._reservation_id = preparation.reservationId || '';
          });
          submit.textContent = 'Adding booking to cart...';
          var addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'content-type': 'application/json', accept: 'application/json' },
            body: JSON.stringify({ items: items })
          });
          var addBody = await addResponse.json();
          if (!addResponse.ok) throw new Error(addBody.description || addBody.message || 'The booking could not be added.');
          notifyFormState(form, 'trade-waste:booking-added');
          window.location.assign('/cart');
        } catch (error) {
          showError(error && error.message ? error.message : 'The booking could not be added. Please try again.');
          delete form.dataset.bookingSubmitting;
          submit.textContent = 'Accept terms and continue to cart';
          updateReadiness();
        }
      });

      updateReadiness();
    });
  }

  function operationsForms() {
    document.querySelectorAll('[data-operations-form]').forEach(function (form) {
      if (form.dataset.operationsReady) return;
      form.dataset.operationsReady = 'true';
      var submit = form.querySelector('[data-form-submit]');
      var errors = form.querySelector('[data-operation-errors]');
      var result = form.querySelector('[data-operation-result]');
      if (!submit || !errors || !result) return;
      var originalLabel = submit.textContent;
      var quoteRequired = form.dataset.requireDeliveryQuote === 'true';
      var quoteReadiness = form.querySelector('[data-operation-readiness]');
      function updateQuoteReadiness() {
        if (!quoteRequired || form.dataset.operationSubmitting === 'true' || form.dataset.operationCompleted === 'true') return;
        var quoteToken = form.querySelector('[data-delivery-quote-token]');
        var ready = Boolean(quoteToken && quoteToken.value);
        submit.disabled = !ready;
        if (quoteReadiness) quoteReadiness.textContent = ready
          ? 'Delivery address checked. Complete the required details and accept the terms to issue your quote.'
          : 'Select and check your delivery address to enable the quote button.';
      }
      form.addEventListener('trade-waste:delivery-state', updateQuoteReadiness);
      form.querySelectorAll('[data-query-param]').forEach(function (field) {
        var value = new URLSearchParams(window.location.search).get(field.dataset.queryParam);
        if (value) field.value = value.slice(0, 120);
      });
      form.querySelectorAll('[data-today-date]').forEach(function (field) {
        if (!field.value) field.value = dateKey(utcDate(nzNowParts().year, nzNowParts().month, nzNowParts().day));
      });
      form.addEventListener('submit', async function (event) {
        event.preventDefault();
        if (form.dataset.operationSubmitting === 'true' || form.dataset.operationCompleted === 'true') return;
        errors.hidden = true;
        result.hidden = true;
        if (!validateTouchedForm(form, true)) return;
        if (form.dataset.requireDeliveryQuote === 'true') {
          var quoteToken = form.querySelector('[data-delivery-quote-token]');
          if (!quoteToken || !quoteToken.value) {
            errors.textContent = 'Select and check your delivery address before issuing the quote.';
            errors.hidden = false;
            return;
          }
        }
        var submittedAt = form.dataset.operationSubmittedAt || new Date().toISOString();
        form.dataset.operationSubmittedAt = submittedAt;
        var submittedAtField = form.querySelector('[data-submitted-at]');
        var acceptedAtField = form.querySelector('[data-terms-accepted-at]');
        if (submittedAtField) submittedAtField.value = submittedAt;
        if (acceptedAtField) acceptedAtField.value = submittedAt;
        form.dataset.operationSubmitting = 'true';
        submit.disabled = true;
        submit.textContent = 'Submitting securely...';
        try {
          var response = await operationRequest(form, form.dataset.operationPath, operationPayload(form));
          operationResult(result, response);
          clearTouchedValidation(form);
          form.dataset.operationCompleted = 'true';
          submit.textContent = 'Submitted';
        } catch (error) {
          if (!showOperationServerValidation(form, error)) {
            errors.textContent = error && error.message ? error.message : 'The request could not be submitted. Please try again.';
            errors.hidden = false;
            errors.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
          }
          submit.disabled = false;
          submit.textContent = originalLabel;
        } finally {
          form.dataset.operationSubmitting = 'false';
          updateQuoteReadiness();
        }
      });
      updateQuoteReadiness();
    });
  }

  function serviceRequestForms() {
    document.querySelectorAll('[data-service-request-form]').forEach(function (form) {
      if (form.dataset.serviceRequestReady) return;
      form.dataset.serviceRequestReady = 'true';
      var findBins = form.querySelector('[data-find-active-bins]');
      var binSelect = form.querySelector('[data-active-bin-select]');
      var binStatus = form.querySelector('[data-active-bin-status]');
      var orderNumber = form.querySelector('[name="orderNumber"]');
      var email = form.querySelector('[name="email"]');
      if (findBins && binSelect && binStatus && orderNumber && email) {
        [orderNumber, email].forEach(function (field) {
          field.addEventListener('input', function () {
            binSelect.replaceChildren();
            var reset = document.createElement('option');
            reset.value = '';
            reset.textContent = 'Find delivered bins first';
            binSelect.appendChild(reset);
            binSelect.disabled = true;
            validateTouchedControl(binSelect, false);
          });
        });
        findBins.addEventListener('click', async function () {
          if (!validateTouchedControls(form, [orderNumber, email], true)) return;
          findBins.disabled = true;
          binStatus.textContent = 'Checking delivered bins…';
          try {
            var headers = { 'content-type': 'application/json', accept: 'application/json' };
            var connection = operationConnection(form, '/api/v1/service-requests/eligible-bins');
            var client = connection.token;
            if (client) headers['x-trade-waste-client'] = client;
            var response = await fetch(connection.url, {
              method: 'POST', headers: headers, credentials: connection.credentials,
              body: JSON.stringify({ orderNumber: orderNumber.value.trim(), email: email.value.trim() })
            });
            var body = await response.json().catch(function () { return {}; });
            if (!response.ok) throw new Error(body.error && body.error.message ? body.error.message : 'Delivered bins could not be loaded.');
            binSelect.replaceChildren();
            var prompt = document.createElement('option');
            prompt.value = '';
            prompt.textContent = body.bins && body.bins.length ? 'Select the physical bin' : 'No delivered bins are available';
            binSelect.appendChild(prompt);
            (body.bins || []).forEach(function (bin) {
              var option = document.createElement('option');
              option.value = bin.id;
              option.textContent = [bin.reference || body.order, bin.binSize, bin.wasteType, bin.unit ? 'unit ' + bin.unit : ''].filter(Boolean).join(' · ');
              binSelect.appendChild(option);
            });
            binSelect.disabled = !(body.bins && body.bins.length);
            validateTouchedControl(binSelect, false);
            binStatus.textContent = body.bins && body.bins.length
              ? 'Select the bin the driver will collect.' : 'No delivered bin is available for this verified order. Contact Trade Waste if this is unexpected.';
          } catch (error) {
            binSelect.disabled = true;
            validateTouchedControl(binSelect, false);
            binStatus.textContent = error && error.message ? error.message : 'Delivered bins could not be loaded.';
          } finally {
            findBins.disabled = false;
          }
        });
      }
      var size = form.querySelector('[data-replacement-size]');
      var waste = form.querySelector('[data-replacement-waste]');
      if (!size || !waste) return;

      function updateWasteOptions() {
        Array.from(waste.options).forEach(function (option) {
          if (!option.value) return;
          var validSizes = (option.dataset.validSizes || '').split('|');
          var available = !size.value || validSizes.indexOf(size.value) !== -1;
          option.disabled = !available;
          option.hidden = !available;
        });
        if (waste.selectedOptions.length && waste.selectedOptions[0].disabled) waste.value = '';
      }

      size.addEventListener('change', updateWasteOptions);
      updateWasteOptions();
    });
  }

  function tradeCustomerPortals() {
    document.querySelectorAll('[data-trade-portal]').forEach(function (portal) {
      if (portal.dataset.tradeReady) return;
      portal.dataset.tradeReady = 'true';
      var form = portal.querySelector('[data-trade-booking-form]');
      var accountStatus = portal.querySelector('[data-trade-account-status]');
      var skipsTarget = portal.querySelector('[data-current-skips]');
      if (!form || !accountStatus || !skipsTarget) return;
      var base = (portal.dataset.operationsApiBase || '/apps/trade-waste').replace(/\/$/, '');
      var fields = form.querySelector('[data-trade-booking-fields]');
      var submit = form.querySelector('[data-form-submit]');
      var errors = form.querySelector('[data-trade-errors]');
      var result = form.querySelector('[data-trade-result]');

      form.addEventListener('input', function () {
        if (form.dataset.tradeSubmitting === 'true') return;
        form.dataset.idempotencyKey = '';
        form.dataset.submittedAt = '';
      });

      function money(cents) {
        return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' }).format(Number(cents || 0) / 100);
      }

      async function json(path, options) {
        var response = await fetch(base + path, Object.assign({ credentials: 'same-origin', headers: { accept: 'application/json' } }, options || {}));
        var body;
        try { body = await response.json(); } catch (_error) { body = {}; }
        if (!response.ok) throw new Error(body.error && body.error.message || 'The trade account request could not be completed.');
        return body;
      }

      function detail(list, name, value) {
        var term = document.createElement('dt');
        term.textContent = name;
        var description = document.createElement('dd');
        description.textContent = value;
        list.append(term, description);
      }

      function renderSkips(skips) {
        skipsTarget.replaceChildren();
        if (!skips.length) {
          var empty = document.createElement('p');
          empty.textContent = 'There are no current skips linked to this account.';
          skipsTarget.appendChild(empty);
          return;
        }
        skips.forEach(function (skip) {
          var card = document.createElement('article');
          card.className = 'tw-current-skip';
          var heading = document.createElement('h3');
          heading.textContent = String(skip.binSize || 'Skip') + ' - ' + String(skip.wasteType || '').replace(/_/g, ' ').toLowerCase();
          var reference = document.createElement('strong');
          reference.textContent = skip.tradeBookingReference || skip.reference;
          var list = document.createElement('dl');
          detail(list, 'Status', String(skip.status || '').toLowerCase().replace(/_/g, ' '));
          detail(list, 'Delivery', skip.deliveryDate || 'To be confirmed');
          detail(list, 'Hire', String(skip.hireDays || 7) + ' days');
          if (skip.billingOrderName) detail(list, 'Invoice', skip.billingOrderName);
          card.append(heading, reference, list);
          skipsTarget.appendChild(card);
        });
      }

      async function loadAccount() {
        try {
          var account = await json('/api/v1/trade/account');
          accountStatus.dataset.approved = 'true';
          accountStatus.textContent = 'Approved account: ' + account.customer.displayName + '. Monthly credit limit: ' + money(account.creditLimitCents) + '.';
          fields.disabled = false;
          renderSkips(account.skips || []);
        } catch (error) {
          accountStatus.dataset.approved = 'false';
          accountStatus.textContent = error && error.message ? error.message : 'This trade account could not be verified.';
          fields.disabled = true;
          submit.disabled = true;
          skipsTarget.textContent = 'Current skips are available only to a verified approved trade account.';
        }
      }

      form.addEventListener('submit', async function (event) {
        event.preventDefault();
        errors.hidden = true;
        result.hidden = true;
        if (fields.disabled || accountStatus.dataset.approved !== 'true') return;
        if (!validateTouchedForm(form, true)) return;
        var quoteToken = form.querySelector('[data-delivery-quote-token]');
        if (!quoteToken || !quoteToken.value) {
          errors.textContent = 'Check the delivery address before confirming the booking.';
          errors.hidden = false;
          return;
        }
        var variant = form.querySelector('[data-trade-variant]');
        var quantity = Number(form.querySelector('[data-trade-quantity]').value);
        var items = [{ variantId: variant.value, quantity: quantity }];
        var missingAddon = '';
        form.querySelectorAll('[data-trade-addon]').forEach(function (input) {
          var addonQuantity = Number(input.value) || 0;
          if (addonQuantity > 0 && !input.dataset.tradeAddon) missingAddon = 'A selected add on is temporarily unavailable. Contact Trade Waste before booking.';
          else if (addonQuantity > 0) items.push({ variantId: input.dataset.tradeAddon, quantity: addonQuantity });
        });
        if (missingAddon) {
          errors.textContent = missingAddon;
          errors.hidden = false;
          return;
        }
        var access = {};
        form.querySelectorAll('[data-trade-access]').forEach(function (field) { access[field.dataset.tradeAccess] = field.checked; });
        access.permitConfirmed = form.querySelector('[data-trade-permit]').checked;
        var now = form.dataset.submittedAt || new Date().toISOString();
        form.dataset.submittedAt = now;
        var key = form.dataset.idempotencyKey || idempotencyKey();
        form.dataset.idempotencyKey = key;
        var payload = {
          idempotencyKey: key,
          items: items,
          deliveryAddress: {
            address1: form.querySelector('[data-delivery-address]').value.trim(),
            address2: form.querySelector('[data-delivery-suburb]').value.trim(),
            city: form.querySelector('[data-delivery-city]').value.trim(),
            postcode: form.querySelector('[data-delivery-postcode]').value.trim(),
            countryCode: 'NZ',
            ...(form.querySelector('[data-delivery-place-id]').value.trim()
              ? { placeId: form.querySelector('[data-delivery-place-id]').value.trim() }
              : {})
          },
          deliveryQuoteToken: quoteToken.value,
          requestedDeliveryDate: form.querySelector('[data-delivery-date]').value,
          deliveryWindow: form.querySelector('[data-trade-window]').value,
          access: access,
          placementInstructions: form.querySelector('[data-trade-placement]').value.trim(),
          purchaseOrderNumber: form.querySelector('[data-trade-po]').value.trim() || undefined,
          typedName: form.querySelector('[data-trade-signer]').value.trim(),
          termsVersion: portal.dataset.termsVersion,
          termsAccepted: true,
          termsAcceptedAt: now,
          submittedAt: now
        };
        form.dataset.tradeSubmitting = 'true';
        submit.disabled = true;
        submit.textContent = 'Confirming booking...';
        try {
          var booking = await json('/api/v1/trade/bookings', {
            method: 'POST',
            headers: { accept: 'application/json', 'content-type': 'application/json', 'idempotency-key': key },
            body: JSON.stringify(payload)
          });
          result.replaceChildren();
          var heading = document.createElement('strong');
          heading.textContent = 'Booking confirmed: ' + booking.reference;
          var text = document.createElement('p');
          text.textContent = 'No upfront payment was taken. This booking will be included in the ' + booking.billingPeriod + ' monthly invoice.';
          result.append(heading, text);
          result.hidden = false;
          result.focus();
          clearTouchedValidation(form);
          form.dataset.idempotencyKey = '';
          form.dataset.submittedAt = '';
          await loadAccount();
        } catch (error) {
          errors.textContent = error && error.message ? error.message : 'The trade booking could not be confirmed.';
          errors.hidden = false;
          errors.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        } finally {
          form.dataset.tradeSubmitting = 'false';
          submit.disabled = fields.disabled;
          submit.textContent = 'Confirm trade booking';
        }
      });

      loadAccount();
    });
  }

  function init() {
    drawer();
    navigationDropdowns();
    sliders();
    reveal();
    productGalleries();
    variantPrices();
    termsScrollGates();
    dateInputs();
    deliveryQuotes();
    addressLookups();
    touchedValidation();
    cartCheckoutHandoff();
    bookingForms();
    operationsForms();
    serviceRequestForms();
    tradeCustomerPortals();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
}());
